import { bbox, haversine, samplePolyline, totalDistance, type LngLat } from "@/utils/polyline";
import { setItemWithinBudget, OVERPASS_BUDGET } from "@/utils/storageBudget";
import { readCache, writeCache } from "@/utils/poiCache";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { corridorOffset } from "@/utils/scenicRoads";
import { STATE_PARKS } from "@/content/stateParks";
import { noteOverpassCells, noteEnrichmentPath } from "@/lib/searchTelemetry";
import {
  tileInPrivateCoverage,
  OUT_OF_COVERAGE_BUDGET_MS,
} from "@/api/overpassCoverage";

// ---------------------------------------------------------------------------
// CLIENT-SIDE Overpass (the "city walks are broken" fix). The `overpass` edge
// function runs on Supabase's cloud IP, which the public Overpass mirrors
// throttle hard — every call times out and returns ZERO features, so walk/bike
// adventure discovery always came back empty (no pleasant city walk, anywhere).
// The browser reaches Overpass directly (CORS is enabled) and gets results
// instantly, so for the latency-sensitive discovery queries we query Overpass
// straight from the client, mirror-fallback included.
// ---------------------------------------------------------------------------
// Overpass endpoints, in priority order. VITE_OVERPASS_ENDPOINT (a dedicated or
// self-hosted instance) wins when set — no code change needed to switch. Then
// the Private.coffee instance (overpass.kumi.systems), which is free, UNLIMITED,
// no rate limits, commercial use OK — far more reliable than overpass-api.de,
// which rate-limits (429s) and was starving walk/scenic discovery. The public
// mirrors stay as last-resort fallbacks.
// Exported for the admin nomination approver, which needs the same list but a
// patient sequential try (60s each) rather than the route-search race.
export const OVERPASS_MIRRORS = [
  ...(import.meta.env.VITE_OVERPASS_ENDPOINT
    ? [import.meta.env.VITE_OVERPASS_ENDPOINT as string]
    : []),
  "https://overpass.kumi.systems/api/interpreter",
  "https://overpass-api.de/api/interpreter",
  "https://overpass.openstreetmap.ru/api/interpreter",
];

// ---------------------------------------------------------------------------
// LOCAL ESCAPE HATCH — unreachable public mirrors (Aug 24, card-copy audit).
//
// On a network where the public mirrors can't be reached, they return no data
// AND cost real wall-clock: measured from the browser on 2026-08-24,
// kumi.systems failed in 271ms, overpass-api.de in 1590ms, but
// overpass.openstreetmap.ru BLACK-HOLES the connection and took 75,027ms to
// fail on TCP timeout. Pass-2 discovery fans out many queries, so a corridor
// whose primary misses the deadline sits in that 75s stall over and over:
// `refining` never clears (so the "Why this route?" panel stays permanently
// ungated), and the tab accumulates hung sockets until every fetch in it —
// Vite module imports included — stops resolving, which makes the NEXT search
// hang on its first `await import(...)` before it can log anything.
//
// Two ways to turn it on, both opt-in and both off by default:
//   - VITE_OVERPASS_SKIP_PUBLIC_MIRRORS=1 in a local env file, or
//   - localStorage["pleasant.dev.skipPublicOverpassMirrors"] = "1", which is
//     honoured ONLY in a dev build (import.meta.env.DEV) so it can never
//     reach a deployed one, and is read per call so it can be flipped
//     mid-session without a reload.
// With neither set — which is every deployed build — this is the shipped
// behaviour, bit-for-bit: the public mirrors stay in the chain.
//
// This does NOT change what counts as measured. A primary failure with the
// flag on still returns [] and is still UNMEASURED, never measured-zero —
// it just reaches that verdict in ~1s instead of ~75s.
//
// Requires VITE_OVERPASS_ENDPOINT: with no primary there is nothing to fall
// back FROM, so skipping the mirrors would leave no endpoint at all. The flag
// is ignored in that case rather than silently disabling Overpass entirely.
// ---------------------------------------------------------------------------
function skipPublicMirrors(): boolean {
  // No primary to fall back FROM → the flag would leave no endpoint at all.
  if (!import.meta.env.VITE_OVERPASS_ENDPOINT) return false;
  if (import.meta.env.VITE_OVERPASS_SKIP_PUBLIC_MIRRORS === "1") return true;
  if (!import.meta.env.DEV) return false;
  try {
    return localStorage.getItem("pleasant.dev.skipPublicOverpassMirrors") === "1";
  } catch {
    return false; // private mode / storage disabled — behave as shipped
  }
}

/** Run an Overpass QL query directly from the browser. Tries each mirror in
 *  order with a timeout; returns parsed elements, or [] on total failure. */
type OverpassEl = { type?: string; id?: number; tags?: Record<string, string>; lat?: number; lon?: number; center?: { lat: number; lon: number }; geometry?: Array<{ lat: number; lon: number }> };

/** Cheap stable hash of an Overpass query, for the durable result cache. */
function overpassQueryHash(q: string): string {
  let h = 5381;
  for (let i = 0; i < q.length; i++) h = ((h << 5) + h + q.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

// ---------------------------------------------------------------------------
// READY-DEADLINE PROPAGATION (Morgan, Aug 22).
//
// THE BUG THIS FIXES. `edgeBudget` below is 23s, because the edge function's
// own REQUEST_DEADLINE_MS is 20s and aborting at 15s taught us nothing except
// that we had killed a live query. Correct in isolation. But generateRoutes
// holds a 25s READY ceiling for the WHOLE search, and enrichment does not
// start until routing, dedupe and injection are done - measured at 3.1s to
// 13.4s. 3s + 23s > 25s, so on a late-starting search the edge call could not
// finish before the ceiling no matter what the network did. Five of seven real
// Sleepy Hollow searches abandoned enrichment; the two that survived were the
// two that started earliest.
//
// THE RULE. The 25s ceiling is the product constraint and is authoritative. No
// single dependency of a search may hold a required window longer than the
// time the search has left. So we do not shorten the edge budget - we refuse
// to START an edge call whose own required window cannot fit in what remains,
// and spend the remaining time on the transport that can.
//
// AMBIENT, NOT THREADED, AND THAT IS A TRADEOFF. overpassDirect has nine call
// sites across four modules and sits under six more helpers. Threading a
// deadline parameter through all of them would touch every scenery path for
// one search-scoped fact. Instead the deadline is set once per generation run
// and read here. The cost: two OVERLAPPING generations share one deadline, and
// the later one wins. That is tolerable because a superseded run's results are
// discarded by isCancelled() anyway - but it is a real limitation, not a
// detail, and a second concurrent consumer of overpassDirect would need this
// revisited.
//
// UNSET MEANS UNCHANGED. With no deadline set, remaining is Infinity and every
// path behaves exactly as it did before - so callers that genuinely have 23s
// keep it (rule: do not globally cripple overpassDirect).
// ---------------------------------------------------------------------------
let ACTIVE_DEADLINE_AT: number | null = null;

/** Arm the READY deadline for the current generation. `null` disarms. */
export function setOverpassDeadline(at: number | null): void {
  ACTIVE_DEADLINE_AT = at;
}

/** Milliseconds left before the READY ceiling; Infinity when unarmed. */
export function overpassRemainingMs(): number {
  return ACTIVE_DEADLINE_AT == null
    ? Number.POSITIVE_INFINITY
    : ACTIVE_DEADLINE_AT - performance.now();
}

// ---------------------------------------------------------------------------
// MIRROR CIRCUIT BREAKER + NATIVE IN-FLIGHT CAP (Aug 28, from a device log).
//
// What the phone log showed: nine `CapacitorHttp post` calls to four Overpass
// hosts, every one timing out (-1001) or refusing to connect (-1004), and
// alongside them
//
//   [degraded] ... budgetMs=21758 elapsedMs=26263
//   [!] UITapGestureRecognizer is blocking its subgraph for 28.49 seconds
//
// The app was untouchable for as long as the enrichment ran over budget.
//
// The cause is that on native there is nothing to abort with. The web branch
// below aborts its fetch when the deadline fires -- its own comment says a
// rejected race "leaves the socket open" and the deadline "has to actually
// cancel the work". `CapacitorHttp.post` has no abort, so on the phone the
// deadline only rejects OUR promise: the native request keeps running to its
// own timeout, and every one of a discovery pass's queries adds more. They
// come back long after anyone wanted them and marshal over the WebView bridge
// while the user is trying to tap.
//
// Two bounds, since cancellation is not available:
//
//   1. A host that fails is not tried again for HOST_COOLDOWN_MS. A dead
//      mirror should cost one timeout per pass, not one per query.
//   2. At most MAX_NATIVE_INFLIGHT native requests exist at once. Over the
//      cap an attempt fails immediately instead of adding another socket that
//      cannot be cancelled.
//
// Both are about wall-clock and responsiveness only. Neither changes what
// counts as measured: a skipped or capped attempt yields no features, and no
// features is UNMEASURED (rule 20), never measured-zero.
// ---------------------------------------------------------------------------
// TWO KINDS OF FAILURE, TWO COOLDOWNS. A host that times out or refuses the
// connection is not there, and asking it again inside the same pass buys
// nothing. A host that answers 429 or 5xx IS there and is telling us to slow
// down -- overpass-api.de rate-limits a query and then serves the next one, so
// benching it for minutes would throw away scenery we could have measured.
// Backing off is about not wasting wall-clock, never about measuring less.
const HOST_DEAD_COOLDOWN_MS = 3 * 60_000;
const HOST_BUSY_COOLDOWN_MS = 5_000;
const MAX_NATIVE_INFLIGHT = 2;

type HostFailure = "dead" | "busy";

/** host -> timestamp (performance.now) until which it is considered down. */
const overpassHostDownUntil = new Map<string, number>();
let nativeInFlight = 0;

function hostOf(url: string): string {
  try { return new URL(url).host; } catch { return url; }
}

function hostIsDown(url: string): boolean {
  const until = overpassHostDownUntil.get(hostOf(url));
  if (until == null) return false;
  if (performance.now() >= until) {
    overpassHostDownUntil.delete(hostOf(url));
    return false;
  }
  return true;
}

/** 429/503 mean "alive, slow down"; anything else we treat as unreachable. */
function classifyStatus(status: number): HostFailure {
  return status === 429 || status === 503 || status === 504 ? "busy" : "dead";
}

/** A thrown error is "busy" only when it carries a status that says so. */
function classifyError(err: unknown): HostFailure {
  const status = (err as { status?: number } | null)?.status;
  return typeof status === "number" ? classifyStatus(status) : "dead";
}

function noteHostFailure(url: string, kind: HostFailure = "dead"): void {
  const ms = kind === "busy" ? HOST_BUSY_COOLDOWN_MS : HOST_DEAD_COOLDOWN_MS;
  const until = performance.now() + ms;
  // Never shorten an existing cooldown: a busy answer arriving after a timeout
  // should not put a host we already know is unreachable back in rotation.
  const current = overpassHostDownUntil.get(hostOf(url)) ?? 0;
  overpassHostDownUntil.set(hostOf(url), Math.max(current, until));
}

function noteHostSuccess(url: string): void {
  overpassHostDownUntil.delete(hostOf(url));
}

// The edge function gets the same treatment, and it matters more than the
// mirrors did. `edgeBudget` is 23s of a 25s search ceiling, spent BEFORE the
// mirrors are tried at all -- and the edge function calls the same private
// Overpass box the client does, so when that box is down the edge call is 23
// guaranteed wasted seconds on every single query. Measured Aug 30:
// budgetMs=24301 elapsedMs=42783 against a 25s ceiling.
//
// Keyed separately from the mirrors: the edge function failing says nothing
// about a public mirror, or the reverse.
const EDGE_KEY = "\u0000edge-fn";

function edgeIsDown(): boolean {
  return hostIsDown(EDGE_KEY);
}

function noteEdgeFailure(): void {
  noteHostFailure(EDGE_KEY, "dead");
}

function noteEdgeSuccess(): void {
  noteHostSuccess(EDGE_KEY);
}

/** Test seam: forget every cooldown and reset the in-flight count. */
export function resetOverpassHostHealth(): void {
  overpassHostDownUntil.clear();
  nativeInFlight = 0;
}

/** Diagnosis only: hosts currently in cooldown. */
export function overpassHostsDown(): string[] {
  const now = performance.now();
  return [...overpassHostDownUntil.entries()]
    .filter(([, until]) => until > now)
    .map(([host]) => host);
}

export async function overpassDirect(
  query: string,
  timeoutMs = 9_000,
  /** Short name for the fate log ("waterside", "scenic", …) — diagnosis only. */
  label?: string,
): Promise<OverpassEl[]> {
  // Durable-cache hit → no network. (OSM data is static; see writeOverpassCache.)
  const ck = `${OVERPASS_CACHE_PREFIX}q:${overpassQueryHash(query)}`;
  const cachedQ = readOverpassCache(ck);
  if (cachedQ) return cachedQ as OverpassEl[];

  // EDGE FUNCTION FIRST (Aug 9, measured). This function used to go straight to
  // the mirrors from the browser, on the grounds that the edge function was
  // throttled from Supabase's cloud IP. That was true of PUBLIC mirrors and
  // stopped being true when OVERPASS_ENDPOINT was pointed at our own server.
  //
  // What the Briarcliff log actually showed: `PRIMARY FAILED
  // (46-224-96-83.sslip.io) — AbortError: Fetch is aborted` on every single run.
  // Not CORS, not a dead server — OUR 9s timeout, firing while our own healthy
  // Overpass chewed on one enormous corridor bbox sent from a laptop in Italy.
  // The same box answers the edge function's tiled queries in ~1.5s. So the
  // request is fine; the place it was being made from was not. Every
  // `[water] points=0` and every scenic=0 traces back here.
  //
  // Falls through to the direct mirrors below if the function errors, so this
  // can only add a path, never remove one.
  // Must exceed the edge function's own 20s REQUEST_DEADLINE_MS, or we abort a
  // call that was about to succeed and learn nothing. (Aug 9: at 15s we killed
  // every corridor query mid-flight and logged "edge fn unreachable" for what
  // was actually a query still running.)
  const edgeBudget = Math.max(timeoutMs, 23_000);
  const t0 = performance.now();
  const remainingAtStart = overpassRemainingMs();

  // Already past the ceiling: the caller cannot use this answer. Returning []
  // here is NOT "no features" — the caller treats an empty Overpass result as
  // UNMEASURED (rule 20), which is exactly what it is.
  if (remainingAtStart <= 0) {
    // eslint-disable-next-line no-console
    console.log(`[overpass] ${label ?? "unlabelled"} SKIPPED — READY deadline already passed`);
    return [];
  }

  // The edge function needs its whole window or it teaches us nothing. If that
  // window does not fit in the time left, do not start it.
  const edgeFits = remainingAtStart >= edgeBudget && !edgeIsDown();
  if (!edgeFits) {
    const why = edgeIsDown()
      ? "edge fn is in cooldown after a recent failure"
      : `${Math.round(remainingAtStart)}ms left < ${edgeBudget}ms required window`;
    // eslint-disable-next-line no-console
    console.log(
      `[overpass] ${label ?? "unlabelled"} BYPASSING edge fn — ${why} → mirrors direct`,
    );
  }
  if (edgeFits) try {
    const edge = await Promise.race([
      supabase.functions.invoke("overpass", { body: { query, label } }),
      new Promise<never>((_, rej) =>
        setTimeout(() => rej(new Error(`edge budget ${edgeBudget}ms`)), edgeBudget),
      ),
    ]);
    if (!edge.error && Array.isArray((edge.data as { elements?: OverpassEl[] })?.elements)) {
      const els = (edge.data as { elements: OverpassEl[] }).elements;
      noteEdgeSuccess();
      noteEnrichmentPath("edge");
      // eslint-disable-next-line no-console
      console.log(
        `[overpass] ${label ?? "unlabelled"} via EDGE in ${Math.round(performance.now() - t0)}ms ` +
        `(${els.length} elements)`,
      );
      // Only bank a NON-empty answer: caching [] would poison this query for 14
      // days on a single bad night (the July starvation bug, learned the hard way).
      if (els.length > 0) writeOverpassCache(ck, els as OverpassEl[]);
      return els;
    }
    // An error is the edge function failing; empty elements is a legitimate
    // answer from a working one, so only the former benches it.
    if (edge.error) noteEdgeFailure();
    // eslint-disable-next-line no-console
    console.log(
      `[overpass] edge fn returned nothing for ${label ?? "unlabelled"} ` +
        `(${edge.error ? String(edge.error) : "empty elements"}) → trying mirrors direct`,
    );
  } catch (err) {
    // Timed out or threw. Do not spend another 23s on it this pass.
    noteEdgeFailure();
    // eslint-disable-next-line no-console
    console.log(
      `[overpass] edge fn unreachable for ${label ?? "unlabelled"} — ${String(err)} ` +
        `→ benched for 3 min, trying mirrors direct`,
    );
  }
  // RACE the mirrors instead of trying them one-by-one (load-time fix). The old
  // sequential loop waited up to `timeoutMs` on a slow/down mirror BEFORE trying
  // the next, so a single stalled mirror added ~12s to every discovery pass and
  // made walk cards crawl in. Now fire all mirrors at once and take the first
  // that returns a valid result; only fall back to [] when ALL fail.
  // Each attempt is bounded by whichever expires first: its own timeout, or the
  // READY deadline. Without the second bound a mirror started at 24s would keep
  // a fetch alive long past the moment its answer stopped being usable.
  const attemptBudget = (): number =>
    Math.max(500, Math.min(timeoutMs, overpassRemainingMs()));

  const attempt = (url: string): Promise<OverpassEl[]> => {
    // A host that just failed is not retried until its cooldown expires. On a
    // pass that runs many queries this is the difference between one timeout
    // and one timeout per query.
    if (hostIsDown(url)) {
      return Promise.reject(new Error(`host in cooldown: ${hostOf(url)}`));
    }
    const budget = attemptBudget();
    // NATIVE APP (profiled on-device): the WebView's origin is
    // capacitor://localhost, which the public Overpass mirrors reject with a
    // CORS 406 — every direct call failed on the phone, so corridor scenery
    // burned its full budget and returned NOTHING while working fine in the
    // browser. CapacitorHttp issues the request from native code (no CORS),
    // making the phone behave like the browser. Raced against a timer since
    // its own timeout options aren't reliable across platforms.
    if (Capacitor.isNativePlatform()) {
      // Nothing here can be cancelled, so refuse to start one we cannot afford
      // to leave running.
      if (nativeInFlight >= MAX_NATIVE_INFLIGHT) {
        return Promise.reject(new Error("native request cap reached"));
      }
      nativeInFlight++;
      let settled = false;
      const native = CapacitorHttp.post({
        url,
        data: "data=" + encodeURIComponent(query),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        connectTimeout: budget,
        readTimeout: budget,
      });
      // Track the REAL end of the native request, not the end of our race.
      // The deadline below stops us waiting; it does not stop the request, and
      // the slot stays taken until the request itself is actually over.
      void native
        .then((res) => {
          settled = true;
          if (res.status === 200) noteHostSuccess(url);
          else noteHostFailure(url, classifyStatus(res.status));
        })
        .catch((err) => {
          settled = true;
          noteHostFailure(url, classifyError(err));
        })
        .finally(() => {
          nativeInFlight = Math.max(0, nativeInFlight - 1);
        });

      const answer = native.then((res) => {
        const els = (res.data as { elements?: OverpassEl[] } | undefined)?.elements;
        if (res.status === 200 && Array.isArray(els)) return els;
        throw new Error("bad status");
      });
      return Promise.race([
        answer,
        new Promise<never>((_, rej) =>
          setTimeout(() => {
            // Our deadline, not the server's. Mark the host down now rather
            // than waiting for a request we have stopped listening to: it is
            // already slower than any caller can use.
            if (!settled) noteHostFailure(url);
            rej(new Error("deadline"));
          }, budget),
        ),
      ]);
    }
    const ctl = new AbortController();
    // ABORTS, not just rejects. A rejected race leaves the socket open; the
    // deadline has to actually cancel the work (Morgan: every network path must
    // be cancellable when the READY deadline arrives).
    const timer = setTimeout(() => ctl.abort(), budget);
    return fetch(url, {
      method: "POST",
      body: "data=" + encodeURIComponent(query),
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      signal: ctl.signal,
    })
      // READ AS TEXT, THEN PARSE (Morgan, Aug 22 — separate defect).
      //
      // The primary answered a JSON-requesting client with `<?xml version=...`,
      // and `res.json()` turned that into `SyntaxError: Unexpected token '<'`.
      // That message names the symptom and hides every fact needed to tell an
      // upstream Overpass error document from a proxy/gateway page from an
      // endpoint serving the wrong format by default. Status, content-type and
      // the first bytes of the body distinguish all three; the SyntaxError
      // distinguishes none of them. Text-then-parse is otherwise identical:
      // same request, same success path, one extra string.
      .then(async (res) => {
        const body = await res.text();
        if (!res.ok) {
          // eslint-disable-next-line no-console
          console.log(
            `[overpass] NON-OK ${new URL(url).host} status=${res.status} ` +
            `content-type=${res.headers.get("content-type") ?? "(none)"} ` +
            `len=${body.length} body[0:200]=${JSON.stringify(body.slice(0, 200))}`,
          );
          const e = new Error(`bad status ${res.status}`) as Error & { status?: number };
          e.status = res.status;
          throw e;
        }
        try {
          return JSON.parse(body);
        } catch {
          // eslint-disable-next-line no-console
          console.log(
            `[overpass] NON-JSON BODY from ${new URL(url).host} status=${res.status} ` +
            `content-type=${res.headers.get("content-type") ?? "(none)"} ` +
            `len=${body.length} body[0:400]=${JSON.stringify(body.slice(0, 400))}`,
          );
          throw new Error("non-json body");
        }
      })
      .then((json) => {
        if (json && Array.isArray(json.elements)) {
          noteHostSuccess(url);
          return json.elements as OverpassEl[];
        }
        throw new Error("no elements");
      })
      .catch((err) => {
        noteHostFailure(url, classifyError(err));
        throw err;
      })
      .finally(() => clearTimeout(timer));
  };
  // PRIMARY-FIRST (Morgan, approved from her 429-filled console log): our own
  // server answers in ~1s when healthy — racing the public mirrors on every
  // query just sprayed 429/504 noise into every run (and, pre-cache-fix,
  // poisoned measurements). Try the Pleasant server ALONE first; only if it
  // fails or times out, race the public mirrors as the fallback they were
  // always meant to be.
  const primary = OVERPASS_MIRRORS[0];
  const fallbacks = skipPublicMirrors()
    ? []
    : import.meta.env.VITE_OVERPASS_ENDPOINT
    ? OVERPASS_MIRRORS.slice(1)
    : OVERPASS_MIRRORS;
  try {
    if (import.meta.env.VITE_OVERPASS_ENDPOINT) {
      try {
        const result = await attempt(primary);
        noteEnrichmentPath("private");
        // eslint-disable-next-line no-console
        console.log(
          `[overpass] ${label ?? "unlabelled"} via PRIMARY in ` +
          `${Math.round(performance.now() - t0)}ms (${result.length} elements)`,
        );
        writeOverpassCache(ck, result as OverpassEl[]);
        return result;
      } catch (err) {
        // NO SILENT VERDICTS (rule 17). This catch was empty, and it is the
        // reason Overpass failure went unnoticed for weeks: our own server
        // dying looked exactly like nothing happening, while the console
        // filled with 429s and refused connections from the PUBLIC mirrors —
        // which read as "Overpass is flaky" rather than "our primary is down".
        //
        // Everything scenic hangs off this. When the corridor fetch starves,
        // scenicScore() has no features and every route reports scenic=0 — so
        // the Scenic honesty gate, the Alternative card's comparison and the
        // "no scenery here" judgements are all made on data nobody collected.
        // Rule 20: a score that was never computed is not a zero.
        // eslint-disable-next-line no-console
        console.log(
          `[overpass] PRIMARY FAILED (${new URL(primary).host}) — ${String(err)} ` +
          `→ falling back to public mirrors, expect 429s and empty scenery`,
        );
      }
    }
    if (fallbacks.length === 0) {
      // Flag is on and the primary already failed above. Say so in the same
      // vocabulary as the all-endpoints-failed path — the verdict is
      // identical (no features, UNMEASURED), only the route to it is shorter.
      // eslint-disable-next-line no-console
      console.log(
        `[overpass] ${label ?? "unlabelled"} — public mirrors disabled locally ` +
        `(dev skip-mirrors flag) → returning no features; ` +
        `every scenic score on this trip is unmeasured, not zero`,
      );
      return [];
    }
    // Skip mirrors already known to be down. When every one of them is in
    // cooldown there is nothing to try, and saying so now costs zero wall-clock
    // instead of four more timeouts -- which is the whole point of the breaker.
    const liveFallbacks = fallbacks.filter((u) => !hostIsDown(u));
    if (liveFallbacks.length === 0) {
      // eslint-disable-next-line no-console
      console.log(
        `[overpass] ${label ?? "unlabelled"} — every mirror is in cooldown ` +
        `(${fallbacks.map(hostOf).join(", ")}) → returning no features; ` +
        `unmeasured, not zero`,
      );
      return [];
    }
    const result = await Promise.any(liveFallbacks.map(attempt));
    noteEnrichmentPath("public-mirror");
    // eslint-disable-next-line no-console
    console.log(
      `[overpass] ${label ?? "unlabelled"} via MIRROR in ` +
      `${Math.round(performance.now() - t0)}ms (${result.length} elements)`,
    );
    writeOverpassCache(ck, result as OverpassEl[]);
    return result;
  } catch (err) {
    noteEnrichmentPath("none");
    // eslint-disable-next-line no-console
    console.log(
      `[overpass] ALL ENDPOINTS FAILED (${fallbacks.length} mirrors) — ${String(err)} ` +
      `→ returning no features; every scenic score on this trip is unmeasured, not zero`,
    );
    return [];
  }
}

/** Walk/bike adventure features (parks, gardens, pedestrian ways, squares,
 *  water, landmarks) in a bbox — the client-side twin of the edge function's
 *  walkAdventureQuery, used so discovery actually returns data. */
function walkAdventureOverpassQuery(b: { minLat: number; minLng: number; maxLat: number; maxLng: number }): string {
  const bb = `${b.minLat},${b.minLng},${b.maxLat},${b.maxLng}`;
  return `[out:json][timeout:25];
(
  way["leisure"~"^(park|garden)$"](${bb});
  way["highway"="pedestrian"](${bb});
  way["place"="square"](${bb});
  way["natural"="water"](${bb});
  way["waterway"](${bb});
  way["landuse"="reservoir"](${bb});
  way["landuse"="cemetery"](${bb});
  way["amenity"="grave_yard"](${bb});
  node["tourism"~"^(attraction|viewpoint|artwork|gallery|museum)$"](${bb});
  node["historic"~"^(monument|memorial|castle|ruins|tower)$"](${bb});
);
out tags center 250;`;
}

/** Major NAMED parks/gardens near a walk corridor — the client-side query for
 *  the "walk through the park" pass (Washington Square, the Tuileries, St James's
 *  Park). Relations + ways, named only, so we thread through real destination
 *  green space rather than a nameless traffic island. */
function walkParkOverpassQuery(b: { minLat: number; minLng: number; maxLat: number; maxLng: number }): string {
  const bb = `${b.minLat},${b.minLng},${b.maxLat},${b.maxLng}`;
  return `[out:json][timeout:25];
(
  way["leisure"~"^(park|garden)$"]["name"](${bb});
  relation["leisure"~"^(park|garden)$"]["name"](${bb});
);
out tags center 80;`;
}

/**
 * Find the best major NAMED park to walk THROUGH on a trip: the one nearest the
 * direct line that sits between the endpoints (a genuine on-the-way park, not a
 * detour to a distant one). Threading a route through its center makes the walk
 * cross the park on its internal paths. Returns the via point + the park's name
 * (for the card: "Through Washington Square Park"). Client-side Overpass (the
 * edge function is throttled). Background use; returns null on any miss.
 */
export async function fetchWalkParkVia(
  start: LngLat,
  end: LngLat,
  opts: { corridorKm?: number } = {},
): Promise<{ via: LngLat; name: string } | null> {
  const corridorKm = opts.corridorKm ?? 0.7;
  const b = bbox([start, end], 0.02);
  const elements = await overpassDirect(walkParkOverpassQuery(b), 9_000, "walkpark");
  if (elements.length === 0) return null;
  let best: { via: LngLat; name: string; off: number } | null = null;
  for (const el of elements) {
    const lat = el.center?.lat ?? el.lat;
    const lon = el.center?.lon ?? el.lon;
    const name = el.tags?.name;
    if (typeof lat !== "number" || typeof lon !== "number" || !name) continue;
    const via: LngLat = [lon, lat];
    const { offKm, t } = corridorOffset(via, start, end);
    if (offKm > corridorKm || t < 0.08 || t > 0.92) continue; // off-axis or hugging an endpoint
    if (!best || offKm < best.off) best = { via, name, off: offKm };
  }
  return best ? { via: best.via, name: best.name } : null;
}

/** Ray-casting point-in-ring test. ring = [[lng,lat], …]. Exported + pure so the
 *  through-park containment is unit-testable without any network. */
export function pointInRing(pt: LngLat, ring: LngLat[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersect =
      (yi > pt[1]) !== (yj > pt[1]) &&
      pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Fraction (0–1) of the route's sampled points that fall inside ANY of the
 *  given rings — how much of the walk genuinely runs through this park.
 *  Pure + exported for tests. */
export function routeFractionInside(coords: LngLat[], rings: LngLat[][]): number {
  if (!coords.length || !rings.length) return 0;
  const N = Math.min(40, coords.length);
  const step = (coords.length - 1) / Math.max(1, N - 1);
  let inside = 0;
  for (let i = 0; i < N; i++) {
    const p = coords[Math.round(i * step)];
    if (rings.some((r) => r.length >= 3 && pointInRing(p, r))) inside++;
  }
  return inside / N;
}

/** Pull the boundary ring(s) out of an Overpass `out geom` element (a closed way
 *  → one ring; a multipolygon relation → its member ways' geometries). */
function ringsFromElement(el: {
  geometry?: Array<{ lat: number; lon: number }>;
  members?: Array<{ type?: string; role?: string; geometry?: Array<{ lat: number; lon: number }> }>;
}): LngLat[][] {
  const rings: LngLat[][] = [];
  if (Array.isArray(el.geometry) && el.geometry.length >= 3) {
    rings.push(el.geometry.map((g) => [g.lon, g.lat] as LngLat));
  }
  for (const m of el.members ?? []) {
    if (m.type === "way" && Array.isArray(m.geometry) && m.geometry.length >= 2) {
      rings.push(m.geometry.map((g) => [g.lon, g.lat] as LngLat));
    }
  }
  return rings;
}

/** Named park / preserve / state-park polygons in a bbox, WITH geometry. */
function parkPolygonQuery(b: { minLat: number; minLng: number; maxLat: number; maxLng: number }): string {
  const bb = `${b.minLat},${b.minLng},${b.maxLat},${b.maxLng}`;
  return `[out:json][timeout:25];
(
  way["leisure"~"^(park|nature_reserve|recreation_ground)$"]["name"](${bb});
  relation["leisure"~"^(park|nature_reserve)$"]["name"](${bb});
  way["boundary"~"^(protected_area|national_park)$"]["name"](${bb});
  relation["boundary"~"^(protected_area|national_park)$"]["name"](${bb});
);
out geom;`;
}

/**
 * Names of the parks / preserves the walk VERIFIABLY runs THROUGH. Fetches the
 * park polygons along the route and tests, LOCALLY (point-in-polygon), what
 * fraction of the route lies inside each — so we only name a park the route
 * genuinely goes through (Morgan: "see if it goes THROUGH state parks, if so,
 * say so"). Far more reliable than Overpass `is_in`, and the containment math is
 * unit-tested. Background use; returns [] on any miss.
 */
export async function fetchThroughParkNames(
  coords: LngLat[],
  opts: { minFraction?: number } = {},
): Promise<string[]> {
  if (!coords || coords.length < 2) return [];
  const minFraction = opts.minFraction ?? 0.12; // ≥12% of the walk inside the park

  // 1) CURATED parks — instant, deterministic, no network. The reliable path:
  // these always resolve, even when Overpass is slow/empty.
  const curated: { name: string; frac: number }[] = [];
  for (const p of STATE_PARKS) {
    const frac = routeFractionInside(coords, p.rings);
    if (frac >= minFraction) curated.push({ name: p.name, frac });
  }

  // 2) Live Overpass search for ANY OTHER park polygons along the route. Best-
  // effort — if it fails OR HANGS (slow/blocked/empty), the curated result still
  // stands. The internal 5s race is critical: without it a hanging Overpass would
  // keep this promise pending until the caller's outer timeout fired and threw
  // the curated parks away with it.
  const dynamic: { name: string; frac: number }[] = [];
  try {
    const b = bbox(coords, 0.01);
    const elements = (await Promise.race([
      overpassDirect(parkPolygonQuery(b)),
      new Promise<never>((_, rej) => setTimeout(() => rej(new Error("overpass-timeout")), 5_000)),
    ])) as Array<Parameters<typeof ringsFromElement>[0] & { tags?: { name?: string } }>;
    for (const el of elements) {
      const name = el.tags?.name;
      if (!name || typeof name !== "string") continue;
      const frac = routeFractionInside(coords, ringsFromElement(el));
      if (frac >= minFraction) dynamic.push({ name: name.trim(), frac });
    }
  } catch {
    /* network/parse failed — curated parks still apply */
  }

  // Largest-coverage first; curated wins ties; de-dupe by name.
  const all = [...curated, ...dynamic].sort((a, c) => c.frac - a.frac);
  const seen = new Set<string>();
  return all.map((h) => h.name).filter((n) => (seen.has(n) ? false : (seen.add(n), true))).slice(0, 3);
}

/** Scenic-tagged or scenic-named drivable ways in a bbox — the client-side twin
 *  of the edge function's "scenic" kind, so discovery actually returns data
 *  (the edge function is throttled from Supabase's cloud IP → 0 features). */
/** NOT CALLED — kept as the reference for what the tiled path must return.
 *
 *  fetchScenicCorridorVias used to send this as one raw query over the whole
 *  corridor bbox. It now goes through fetchOverpassTiles(tiles, "scenic"), so
 *  the query that actually runs is `scenicQuery` in supabase/functions/overpass.
 *  Those two are NOT equivalent today: the deployed edge version matches
 *  `scenic|byway|corniche|coast(al)? (road|highway|route)` and drops
 *  Parkway / Skyline / Overlook / Shore.
 *
 *  That matters more than it looks. Neither importer reads OSM route relations,
 *  so scenic byways are absent from scenic_features (measured Aug 31: 1
 *  byway-named row in the whole western-Iowa corridor, 5 in the Hudson Valley,
 *  against 314 and 497 `type=route`+`route=road` relations live). Until the
 *  relation import lands, THIS NAME MATCH IS THE ONLY BYWAY DISCOVERY IN THE
 *  SYSTEM. Narrowing it silently narrows the product.
 *
 *  Delete this only when the edge query covers the same terms, or when the
 *  relation import makes the name heuristic redundant. */
function scenicOverpassQuery(b: { minLat: number; minLng: number; maxLat: number; maxLng: number }): string {
  const bb = `${b.minLat},${b.minLng},${b.maxLat},${b.maxLng}`;
  return `[out:json][timeout:25];
(
  way["scenic"="yes"]["highway"](${bb});
  way["highway"]["name"~"Scenic|Byway|Parkway|Skyline|Overlook|Coastal|Shore",i](${bb});
);
out geom 200;`;
}

/** Drivable roads that physically hug the coastline or a major river (within
 *  ~250m) in a bbox — the durable "follows the shore" signal, no curation
 *  needed.
 *
 *  KNOWN BROKEN, Aug 9, and deliberately NOT changed tonight. Measured directly
 *  against our own server minutes apart:
 *      way["waterway"="river"](hudson bbox); out count;   → 71 ways, ~1s
 *      ...->.w; way["highway"](around.w:250); out count;  → empty response
 *  The `around` join fails; a plain query over the same box is instant. In the
 *  app that shows up as `raw label=waterside elements=0 ms=20001`.
 *
 *  It is a REGRESSION, not a permanent flaw — yesterday this path produced two
 *  scenic-corridor routes in ~5s. Prime suspect is the box itself:
 *  docker-compose sets OVERPASS_SPACE=8000000000 (8 GB) on a CPX22 with 4 GB of
 *  RAM (SUBMIT_TONIGHT.md), which would fail memory-hungry joins while leaving
 *  indexed lookups fast. Check `free -h` and `docker logs overpass` before
 *  rewriting the query — the query may be innocent.
 *
 *  Tonight's fix is upstream in generateRoutes: this call no longer blocks the
 *  other discovery sources, so its failure costs its own turn and nothing more. */
function watersideOverpassQuery(b: { minLat: number; minLng: number; maxLat: number; maxLng: number }): string {
  const bb = `${b.minLat},${b.minLng},${b.maxLat},${b.maxLng}`;
  const DRIVE = "^(primary|secondary|tertiary|unclassified|residential|trunk)$";
  return `[out:json][timeout:25];
(
  way["natural"="coastline"](${bb});
  way["waterway"="river"](${bb});
  way["natural"="water"](${bb});
)->.water;
(
  way["highway"~"${DRIVE}"](around.water:250);
);
out geom 300;`;
}

/**
 * Discover scenic-tagged roads ALONG a route axis straight from OpenStreetMap —
 * no curated entry required. Asks the `overpass` edge function (kind "scenic")
 * for ways tagged scenic=yes or named like a scenic byway within a box around
 * the trip, then keeps only those that sit near the straight start→end line and
 * project between the endpoints (a genuine mid-trip detour). Returns a via point
 * per distinct scenic road to inject so the engine can route a scenic candidate.
 * Background use only — never gate first paint on it. Returns [] on any failure.
 */
/** Tiles that follow the start->end axis, covering the SAME ground the single
 *  bbox covered, in pieces.
 *
 *  A bbox over two distant points is mostly corners. NYC->Boston spans
 *  1.66 deg of latitude and 2.94 of longitude: 5.35 deg^2 of rectangle for a
 *  corridor that is a diagonal line through it. Slicing along the axis and
 *  keeping the same 0.05 deg pad gives 8 tiles of ~0.14 deg^2 — 1.15 deg^2 in
 *  total, 4.6x less ground, and not one square degree of it is ground the old
 *  query would have kept.
 *
 *  THE PAD IS 0.05 DEG BECAUSE THAT IS WHAT THE OLD QUERY USED. It is tempting
 *  to pad by the caller's `corridorKm` instead, since that is the width the
 *  filter keeps — but corridorKm defaults to 30 (0.27 deg), five times wider
 *  than the bbox ever was. Padding to it would quietly widen discovery while
 *  claiming to be a tiling change, and on a near-horizontal corridor like
 *  Toronto->Maine it is worse than the original: 8.4 deg^2 against 3.43.
 *  Measured, not guessed. If the corridor SHOULD be wider than 0.05 deg, that
 *  is a real product change and deserves its own before/after, not a rename. */
const CORRIDOR_TILE_PAD_DEG = 0.05;
const MAX_CORRIDOR_TILES = 10;
const MIN_CORRIDOR_TILE_KM = 40;
/** Interior tile joints overlap by this much so they touch without gapping. */
const EDGE_OVERLAP_DEG = 0.005;

export function corridorTiles(
  start: LngLat,
  end: LngLat,
): { south: number; west: number; north: number; east: number }[] {
  const spanM = haversine(start, end);
  if (!Number.isFinite(spanM) || spanM <= 0) return [];
  const spanKm = spanM / 1000;
  const tileKm = Math.max(MIN_CORRIDOR_TILE_KM, spanKm / MAX_CORRIDOR_TILES);
  const n = Math.max(1, Math.min(MAX_CORRIDOR_TILES, Math.ceil(spanKm / tileKm)));
  const padDeg = CORRIDOR_TILE_PAD_DEG;
  // PAD OUTWARD, NOT AT EVERY JOINT. The pad exists to cover ground beside the
  // line and just past its ends — it is not wanted at an interior tile
  // boundary, where both neighbours would apply it and the corridor pays for
  // the same strip twice. Caught by corridorTiles.test.ts: a due-north
  // corridor came to 0.50 deg^2 against a 0.41 deg^2 bbox, because ten tiles
  // each added 0.05 at both ends of a line with no width to absorb it.
  // Diagonals hid it; their bbox is large enough to swallow the excess.
  //
  // Interior joints get EDGE_OVERLAP_DEG instead — enough that tiles touch,
  // small enough to be noise. Overpass returns every way INTERSECTING a bbox,
  // so a way crossing a seam comes back from both neighbours and
  // fetchOverpassTiles dedupes it by type:id; the overlap is belt-and-braces,
  // not load-bearing. It does mean the summed tile area slightly exceeds the
  // old bbox on a thin corridor — same ground twice, never new ground.
  // "Along the axis" is the dominant span: for a north-south corridor that is
  // latitude, for an east-west one longitude. Crude for a 45-degree diagonal,
  // where both directions are partly along-axis, but it errs toward the old
  // behaviour there and the invariant test covers both extremes.
  const lonDominant = Math.abs(end[0] - start[0]) >= Math.abs(end[1] - start[1]);
  const at = (f: number): LngLat => [
    start[0] + (end[0] - start[0]) * f,
    start[1] + (end[1] - start[1]) * f,
  ];
  const tiles: { south: number; west: number; north: number; east: number }[] = [];
  for (let i = 0; i < n; i += 1) {
    const a = at(i / n);
    const b = at((i + 1) / n);
    // Along the axis: full pad only where this tile is an outer end.
    const lo = i === 0 ? padDeg : EDGE_OVERLAP_DEG;
    const hi = i === n - 1 ? padDeg : EDGE_OVERLAP_DEG;
    const alongLo = lonDominant ? (a[0] <= b[0] ? lo : hi) : (a[1] <= b[1] ? lo : hi);
    const alongHi = lonDominant ? (a[0] <= b[0] ? hi : lo) : (a[1] <= b[1] ? hi : lo);
    tiles.push({
      south: Math.min(a[1], b[1]) - (lonDominant ? padDeg : alongLo),
      north: Math.max(a[1], b[1]) + (lonDominant ? padDeg : alongHi),
      west: Math.min(a[0], b[0]) - (lonDominant ? alongLo : padDeg),
      east: Math.max(a[0], b[0]) + (lonDominant ? alongHi : padDeg),
    });
  }
  // TILING A THIN CORRIDOR IS A LOSS. Splitting only pays when the bbox is
  // mostly corners; on a north-south or east-west run there are no corners to
  // save and the tiles sum to the same ground, so all we buy is ten round
  // trips where one would do. Measured shape: cost is ~800ms per request plus
  // ~1,850ms per square degree, which makes a thin 0.41 deg^2 corridor ~1.6s
  // whole against ~4.4s in ten tiles at TILE_CONCURRENCY 2.
  //
  // So: keep the split only when it actually reduces the ground. The geometry
  // already tells us — no timing model needed, and nothing to re-tune when the
  // server changes. 0.7 is loose on purpose: every real diagonal measured came
  // in at 0.12-0.35x, every degenerate one at 1.00-1.02x. Nothing lands near
  // the line.
  const outer = bbox([start, end], CORRIDOR_TILE_PAD_DEG);
  const outerArea = (outer.maxLat - outer.minLat) * (outer.maxLng - outer.minLng);
  const tiledArea = tiles.reduce(
    (a, t) => a + (t.north - t.south) * (t.east - t.west), 0);
  if (outerArea > 0 && tiledArea > outerArea * 0.7) {
    return [{ south: outer.minLat, west: outer.minLng, north: outer.maxLat, east: outer.maxLng }];
  }
  return tiles;
}

export async function fetchScenicCorridorVias(
  start: LngLat,
  end: LngLat,
  opts: { corridorKm?: number; max?: number } = {},
): Promise<LngLat[]> {
  const corridorKm = opts.corridorKm ?? 30;
  const max = opts.max ?? 2;
  // TILED, ALONG THE AXIS (Aug 31). This was one bbox over the whole corridor
  // with `out geom 200`, and it was the most expensive query the app made:
  // measured twice isolated against our own box at 10,919 ms and 11,485 ms for
  // NYC->Boston, hitting the 200 cap every time.
  //
  // Cost is not fixed per query, it is area. Measured on the same box, name
  // regex only, paced so nothing overlapped:
  //
  //     0.01 deg^2 ->    934 ms        1.35 deg^2 ->  3,249 ms
  //     0.16 deg^2 ->  1,441 ms        5.35 deg^2 -> 10,747 ms
  //
  // ~800 ms of round trip plus ~1,850 ms per square degree. So the fix is to
  // ask for less ground per request and let the tiles run concurrently.
  //
  // TWO WINS, AND NEITHER CHANGES WHAT COMES BACK:
  //
  //   1. The old bbox is the rectangle spanning start..end, and on a diagonal
  //      corridor it is mostly corners — ground the `offKm <= corridorKm`
  //      filter below discards anyway. Tiles follow the axis at the SAME
  //      0.05 deg pad, so NYC->Boston goes from 5.35 deg^2 in one request to
  //      1.15 deg^2 across eight. Same ground kept, 4.6x less fetched.
  //   2. `out geom 200` is a CAP, not a count — every measured run returned
  //      exactly 200, so the two vias below were picked from an arbitrary
  //      truncated set. Per-tile caps give the corridor N x the headroom.
  //
  // Bounded to ~10 tiles so a transcontinental trip does not turn into fifty
  // round trips; on a long trip each tile is bigger, still far under the whole
  // rectangle. fetchOverpassTiles brings per-cell caching, the coverage
  // verdict, and TILE_CONCURRENCY — the box degrades badly under unbounded
  // concurrency, so the limit matters as much as the tiling.
  const tiles = corridorTiles(start, end);
  const elements = await fetchOverpassTiles(tiles, "scenic");
  const cands: { via: LngLat; off: number }[] = [];
  for (const el of elements) {
    if (el.type !== "way" || !Array.isArray(el.geometry) || el.geometry.length < 1) continue;
    const g = el.geometry[Math.floor(el.geometry.length / 2)]; // mid of the scenic way
    const via: LngLat = [g.lon, g.lat];
    const { offKm, t } = corridorOffset(via, start, end);
    if (offKm > corridorKm || t < 0.1 || t > 0.9) continue; // off-axis or hugging an endpoint
    cands.push({ via, off: offKm });
  }
  cands.sort((a, c) => a.off - c.off); // closest to the direct line first
  return cands.slice(0, max).map((c) => c.via);
}

/**
 * Discover roads that physically HUG the coast or a major river along a route —
 * the durable "follows the shore" signal, no tag or curation required. Asks the
 * `overpass` edge function (kind "waterside") for drivable roads within 250m of
 * coastline/rivers, then keeps the ones on the route axis. Prefers the LONGER
 * waterside roads (a road that parallels the water for miles beats one that just
 * crosses a bridge). Returns a via point per road. Background use only.
 */
export async function fetchWatersideCorridorVias(
  start: LngLat,
  end: LngLat,
  opts: { corridorKm?: number; max?: number } = {},
): Promise<LngLat[]> {
  const corridorKm = opts.corridorKm ?? 35; // coast roads sit a bit off the direct line
  const max = opts.max ?? 2;
  // THE QUERY BOX MUST BE AS WIDE AS THE LOOKING (Morgan, Aug 2: "the routes
  // have to look out at least half the distance horizontally"). The corridor
  // filter below honored her rule; this bbox didn't — 0.05° pads ~5km around
  // the direct line, so the Overpass query never CONTAINED water sitting
  // 20–50km west, and [water] logged points=0 on every trip regardless of
  // corridorKm. Filtering a box that excludes the lake finds no lake. Pad the
  // fetch to the full corridor width (degrees ≈ km/111).
  const b = bbox([start, end], Math.max(0.05, corridorKm / 111));
  // Query Overpass DIRECTLY from the client — the edge function is throttled
  // from Supabase's cloud IP and returns 0 features (see overpassDirect).
  const elements = await overpassDirect(watersideOverpassQuery(b), 9_000, "waterside");
  // Collect waterside points ALONG the whole axis, then return an ORDERED string
  // of them (one per progress-bin) so the route can be threaded down the coast —
  // a single waypoint just nips past one point and rejoins the highway; a string
  // of ordered coastal points forces a sustained coastal route.
  const TLO = 0.08, THI = 0.92;
  const pts: { p: LngLat; t: number; off: number }[] = [];
  for (const el of elements) {
    if (el.type !== "way" || !Array.isArray(el.geometry) || el.geometry.length < 1) continue;
    const geo = el.geometry;
    const step = Math.max(1, Math.floor(geo.length / 3)); // sample a few points per way
    for (let i = 0; i < geo.length; i += step) {
      const p: LngLat = [geo[i].lon, geo[i].lat];
      const { offKm, t } = corridorOffset(p, start, end);
      if (offKm <= corridorKm && t > TLO && t < THI) pts.push({ p, t, off: offKm });
    }
  }
  if (pts.length === 0) return [];
  // Bin by progress along the axis; keep the closest-to-water/axis point per bin.
  const bins = new Map<number, { p: LngLat; off: number }>();
  for (const pt of pts) {
    const bin = Math.min(max - 1, Math.floor(((pt.t - TLO) / (THI - TLO)) * max));
    const prev = bins.get(bin);
    if (!prev || pt.off < prev.off) bins.set(bin, { p: pt.p, off: pt.off });
  }
  return [...bins.entries()].sort((a, c) => a[0] - c[0]).map(([, v]) => v.p);
}

/**
 * Discover pedestrian "adventure" detours for a WALK or CITY BIKE ride — the
 * green space, waterfront, pedestrian squares, and landmarks that make a walk
 * worth a few extra minutes. Asks the `overpass` edge function (kind
 * "walkadventure") for those features in the trip bbox, keeps the ones near the
 * route axis, and returns an ORDERED string of via points (one per progress-bin)
 * so the route is threaded THROUGH the nice bits rather than nipping past one.
 * Background use only; corridor scale is small (walks are short).
 */
export async function fetchWalkAdventureVias(
  start: LngLat,
  end: LngLat,
  opts: { corridorKm?: number; max?: number } = {},
): Promise<LngLat[]> {
  const corridorKm = opts.corridorKm ?? 1.0; // walks: a detour ~1km off the line is fine
  const max = opts.max ?? 3;
  const b = bbox([start, end], 0.02); // pad ~2km around the (small) corridor
  // Query Overpass DIRECTLY from the client — the edge function times out from
  // Supabase's cloud IP and returns nothing (see overpassDirect above), which is
  // exactly why city walks never surfaced a pleasant route.
  const elements = await overpassDirect(walkAdventureOverpassQuery(b), 9_000, "walkadventure");
  // eslint-disable-next-line no-console
  console.log(`[walkadv] overpass returned ${elements.length} features (corridor ${corridorKm.toFixed(1)}km)`);
  if (elements.length === 0) return [];
  const TLO = 0.1, THI = 0.9;
  const pts: { p: LngLat; t: number; off: number }[] = [];
  for (const el of elements) {
    // Node features carry lat/lon directly; way/area features carry `center`.
    const lat = el.lat ?? el.center?.lat;
    const lon = el.lon ?? el.center?.lon;
    if (typeof lat !== "number" || typeof lon !== "number") continue;
    const p: LngLat = [lon, lat];
    const { offKm, t } = corridorOffset(p, start, end);
    if (offKm <= corridorKm && t > TLO && t < THI) pts.push({ p, t, off: offKm });
  }
  // eslint-disable-next-line no-console
  console.log(`[walkadv] ${pts.length} features on-corridor (of features within bbox)`);
  if (pts.length === 0) return [];
  // Bin by progress along the axis; keep the closest-to-axis feature per bin so
  // the detour is a smooth wander, not a zig-zag.
  const bins = new Map<number, { p: LngLat; off: number }>();
  for (const pt of pts) {
    const bin = Math.min(max - 1, Math.floor(((pt.t - TLO) / (THI - TLO)) * max));
    const prev = bins.get(bin);
    if (!prev || pt.off < prev.off) bins.set(bin, { p: pt.p, off: pt.off });
  }
  const vias = [...bins.entries()].sort((a, c) => a[0] - c[0]).map(([, v]) => v.p);
  // eslint-disable-next-line no-console
  console.log(`[walkadv] threading ${vias.length} via points`);
  return vias;
}

/** LINEAR walkable scenic features (greenways, esplanades, promenades, quais,
 *  riverside paths) WITH geometry, so a walk can FOLLOW one instead of detouring
 *  to a point. Pulls named scenic paths plus the water edges to follow. */
function walkLinearScenicQuery(b: { minLat: number; minLng: number; maxLat: number; maxLng: number }): string {
  const bb = `${b.minLat},${b.minLng},${b.maxLat},${b.maxLng}`;
  return `[out:json][timeout:25];
(
  way["highway"~"^(footway|path|pedestrian|cycleway)$"]["name"~"greenway|esplanade|promenade|quai|riverwalk|riverside|boardwalk|waterfront|sentier|berge",i](${bb});
  relation["route"="foot"](${bb});
  way["natural"="coastline"](${bb});
  way["waterway"="riverbank"](${bb});
);
out geom 200;`;
}

/**
 * LINEAR-FOLLOW walk discovery (Morgan: walks looped/spurred because they detoured
 * to POINT landmarks). Instead, find a linear scenic feature — a greenway, a
 * riverside promenade, the Seine quais, the East River Esplanade — that runs ALONG
 * the trip axis, and return an ORDERED STRING of points down its on-axis stretch so
 * the route FOLLOWS it cleanly (the same model the driving coastline pass uses). No
 * out-and-back, so no loops/dead-ends. Returns [] when no feature runs a meaningful
 * stretch along the corridor (then the caller falls back to point features).
 */
export async function fetchWalkLinearScenicVias(
  start: LngLat,
  end: LngLat,
  opts: { corridorKm?: number; max?: number; minSpanFrac?: number } = {},
): Promise<LngLat[]> {
  const corridorKm = opts.corridorKm ?? 1.2;
  const max = opts.max ?? 6; // several points so the route hugs the feature
  const minSpanFrac = opts.minSpanFrac ?? 0.18; // must run >=18% of the trip along-axis
  const b = bbox([start, end], 0.02);
  const elements = await overpassDirect(walkLinearScenicQuery(b), 9_000, "walklinear");
  // For each linear way, keep the vertices that sit ON the trip axis (near the
  // line AND between the endpoints), then pick the feature whose on-axis run
  // spans the LARGEST stretch of the trip — that's the one worth following.
  let best: { pts: { p: LngLat; t: number; off: number }[]; span: number } | null = null;
  for (const el of elements) {
    if (!Array.isArray(el.geometry) || el.geometry.length < 2) continue;
    const onAxis: { p: LngLat; t: number; off: number }[] = [];
    for (const g of el.geometry) {
      const p: LngLat = [g.lon, g.lat];
      const { offKm, t } = corridorOffset(p, start, end);
      if (offKm <= corridorKm && t > 0.04 && t < 0.96) onAxis.push({ p, t, off: offKm });
    }
    if (onAxis.length < 2) continue;
    const ts = onAxis.map((x) => x.t);
    const span = Math.max(...ts) - Math.min(...ts);
    if (!best || span > best.span) best = { pts: onAxis, span };
  }
  // eslint-disable-next-line no-console
  console.log(`[walklin] best linear feature spans ${best ? (best.span * 100).toFixed(0) : 0}% of the trip`);
  if (!best || best.span < minSpanFrac) return [];
  // Ordered string of points down the feature's on-axis run, evenly spaced.
  best.pts.sort((a, c) => a.t - c.t);
  const n = Math.min(max, best.pts.length);
  const out: LngLat[] = [];
  const step = n > 1 ? (best.pts.length - 1) / (n - 1) : 0;
  for (let i = 0; i < n; i++) out.push(best.pts[Math.round(i * step)].p);
  return out;
}

/** Footpaths / trails INSIDE a park or preserve (map_to_area on the park, then
 *  foot/bike ways within it). These are car-free park trails — exactly what a
 *  walk should follow when one runs alongside the road. */
function walkParkTrailQuery(b: { minLat: number; minLng: number; maxLat: number; maxLng: number }): string {
  const bb = `${b.minLat},${b.minLng},${b.maxLat},${b.maxLng}`;
  return `[out:json][timeout:25];
(
  way["leisure"~"^(park|nature_reserve|recreation_ground)$"](${bb});
  relation["leisure"~"^(park|nature_reserve)$"](${bb});
  way["boundary"="protected_area"](${bb});
  relation["boundary"="protected_area"](${bb});
)->.pk;
.pk map_to_area ->.pa;
(
  way["highway"~"^(footway|path|cycleway|track|bridleway)$"](area.pa);
);
out geom 150;`;
}

/**
 * PARK-TRAIL follow: when a footpath INSIDE a park/preserve runs along the trip
 * axis, return an ordered string of points down its on-axis run so the walk
 * FOLLOWS the trail instead of the road beside it (Morgan: "I could go in the
 * park that runs along the road"). Same follow model as the linear-scenic pass,
 * but the candidate trails come from inside a park — car-free by definition.
 * Returns [] when no park trail runs a meaningful stretch along the corridor.
 */
export async function fetchWalkParkTrailVias(
  start: LngLat,
  end: LngLat,
  opts: { corridorKm?: number; max?: number; minSpanFrac?: number } = {},
): Promise<LngLat[]> {
  const corridorKm = opts.corridorKm ?? 0.8; // trail must run CLOSE to the road it replaces
  const max = opts.max ?? 6;
  const minSpanFrac = opts.minSpanFrac ?? 0.22; // >=22% of the trip along-axis
  const b = bbox([start, end], 0.02);
  let elements: Array<{ geometry?: Array<{ lat: number; lon: number }> }>;
  try {
    elements = (await overpassDirect(walkParkTrailQuery(b), 9_000, "walkparktrail")) as typeof elements;
  } catch {
    return [];
  }
  if (!elements || elements.length === 0) return [];
  return followOnAxisLinear(elements, start, end, { corridorKm, max, minSpanFrac });
}

/**
 * Pure follow-the-best-linear-feature core (extracted so it's unit-testable
 * without a network call). Given linear OSM ways with geometry, keep each way's
 * vertices that sit ON the trip axis (near the start→end line AND between the
 * endpoints), pick the way whose on-axis run spans the LARGEST stretch of the
 * trip, and return an evenly-spaced, ordered string of points down it. Returns
 * [] when no way runs at least `minSpanFrac` of the trip along-axis.
 */
export function followOnAxisLinear(
  elements: Array<{ geometry?: Array<{ lat: number; lon: number }> }>,
  start: LngLat,
  end: LngLat,
  opts: { corridorKm: number; max: number; minSpanFrac: number },
): LngLat[] {
  const { corridorKm, max, minSpanFrac } = opts;
  let best: { pts: { p: LngLat; t: number }[]; span: number } | null = null;
  for (const el of elements) {
    if (!Array.isArray(el.geometry) || el.geometry.length < 2) continue;
    const onAxis: { p: LngLat; t: number }[] = [];
    for (const g of el.geometry) {
      const p: LngLat = [g.lon, g.lat];
      const { offKm, t } = corridorOffset(p, start, end);
      if (offKm <= corridorKm && t > 0.04 && t < 0.96) onAxis.push({ p, t });
    }
    if (onAxis.length < 2) continue;
    const ts = onAxis.map((x) => x.t);
    const span = Math.max(...ts) - Math.min(...ts);
    if (!best || span > best.span) best = { pts: onAxis, span };
  }
  if (!best || best.span < minSpanFrac) return [];
  best.pts.sort((a, c) => a.t - c.t);
  const n = Math.min(max, best.pts.length);
  const out: LngLat[] = [];
  const step = n > 1 ? (best.pts.length - 1) / (n - 1) : 0;
  for (let i = 0; i < n; i++) out.push(best.pts[Math.round(i * step)].p);
  return out;
}

/**
 * Discover a NEAR-DIRECT calm route — the driving "build it ourselves" pass for
 * when Mapbox only returns the arterial/highway line and no curated road is near
 * (the "no calmer route on this trip" gap). Asks the `overpass` edge function
 * (kind "calmcorridor") for quiet low-class roads, keeps the ones running CLOSE
 * to the direct line (tight corridor so the detour stays small), and returns an
 * ordered string of via points so the route threads down the quiet parallel
 * streets rather than nipping past one. Background use only.
 */
export async function fetchCalmCorridorVias(
  start: LngLat,
  end: LngLat,
  opts: { corridorKm?: number; max?: number } = {},
): Promise<LngLat[]> {
  const corridorKm = opts.corridorKm ?? 1.5; // keep it NEAR-direct: small detour
  const max = opts.max ?? 4;
  // TILE the direct corridor (small bboxes strung along the start→end line)
  // instead of ONE giant bbox. The calm query caps at 400 roads per bbox, so on
  // a long trip a single 50-mile bbox returned 400 roads scattered across the
  // whole county — almost none within the 1.5km corridor below — and produced
  // ZERO vias, so the calm route never built (Briarcliff→Rhinebeck). Tiling
  // returns the quiet roads that actually sit near the route axis.
  const tiles = dedupeTiles(routeTiles([start, end], 8));
  let elements: Array<{ type?: string; geometry?: Array<{ lat: number; lon: number }> }> = [];
  try {
    elements = (await fetchOverpassTiles(tiles, "calmcorridor")) as typeof elements;
  } catch {
    return [];
  }
  if (elements.length === 0) return [];
  const TLO = 0.1, THI = 0.9;
  const pts: { p: LngLat; t: number; off: number }[] = [];
  for (const el of elements) {
    if (el.type !== "way" || !Array.isArray(el.geometry) || el.geometry.length < 1) continue;
    const geo = el.geometry;
    const step = Math.max(1, Math.floor(geo.length / 3));
    for (let i = 0; i < geo.length; i += step) {
      const p: LngLat = [geo[i].lon, geo[i].lat];
      const { offKm, t } = corridorOffset(p, start, end);
      if (offKm <= corridorKm && t > TLO && t < THI) pts.push({ p, t, off: offKm });
    }
  }
  if (pts.length === 0) return [];
  // Bin by progress; keep the closest-to-axis quiet point per bin so the detour
  // is a smooth parallel run, not a zig-zag.
  const bins = new Map<number, { p: LngLat; off: number }>();
  for (const pt of pts) {
    const bin = Math.min(max - 1, Math.floor(((pt.t - TLO) / (THI - TLO)) * max));
    const prev = bins.get(bin);
    if (!prev || pt.off < prev.off) bins.set(bin, { p: pt.p, off: pt.off });
  }
  return [...bins.entries()].sort((a, c) => a[0] - c[0]).map(([, v]) => v.p);
}

// RETIRED Aug 23 — batching by tile SET is what prevented five overlapping
// routes from sharing anything. Superseded by the per-cell path below
// (fetchOneTileCell + TILE_FETCH_CONCURRENCY). Kept named here only so the
// history reads: the batch size was never the problem, the cache key was.
// const OVERPASS_EDGE_BATCH_SIZE = 6;
// const OVERPASS_EDGE_CONCURRENCY = 2;

async function mapLimit<T, R>(items: T[], limit: number, fn: (x: T, i: number) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;

  async function worker() {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i], i);
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

/**
 * Route-aware tiling for Overpass queries.
 *
 * Why: Overpass is bbox-scoped and capped (~200–300 elements per query).
 * On a 600 km route a single bbox covers an enormous area and the cap
 * means almost no elements land near the route corridor — parks and water
 * bodies come back as 0 even when the route does pass them.
 *
 * Fix: walk the route polyline in ~`tileKm` chunks. The browser ships the
 * tile list to our `overpass` edge function, which fans out server-side
 * (no CORS, no per-browser throttling, mirror fallback + retries) and
 * returns merged Overpass elements. Each tile is small enough that the
 * 200–300 element cap is plenty for that slice of the corridor.
 */
function routeTiles(
  coords: LngLat[],
  tileKm = 12,
): { south: number; west: number; north: number; east: number }[] {
  if (coords.length < 2) return [];
  const totalM = totalDistance(coords);
  const tileM = tileKm * 1000;
  const n = Math.max(1, Math.ceil(totalM / tileM));
  // Densify so each tile has enough vertices to form a real bbox.
  const dense = samplePolyline(coords, 250);
  const perTile = Math.max(2, Math.ceil(dense.length / n));
  const tiles: { south: number; west: number; north: number; east: number }[] = [];
  for (let i = 0; i < dense.length; i += perTile) {
    // Overlap by 1 vertex so tile bboxes touch.
    const slice = dense.slice(i, Math.min(dense.length, i + perTile + 1));
    if (slice.length < 2) continue;
    const tb = bbox(slice, 0.005);
    tiles.push({ south: tb.minLat, west: tb.minLng, north: tb.maxLat, east: tb.maxLng });
  }
  return tiles;
}

type Tile = { south: number; west: number; north: number; east: number };

/** Collapse tiles whose centers land in the same ~4 km grid cell. Heavily
 *  overlapping route candidates (all sharing the same start/end corridor)
 *  otherwise produce the SAME tile a dozen times — each one a separate
 *  Overpass query. Deduping loses no coverage (overlapping tiles return the
 *  same elements, which fetchOverpassTiles already merges by id) but cuts the
 *  query count from "dozens" to "the union corridor". This is the core fix
 *  for multi-minute route generation once Valhalla doubled the pool. */
function dedupeTiles(tiles: Tile[]): Tile[] {
  const seen = new Set<string>();
  const out: Tile[] = [];
  for (const t of tiles) {
    const key = tileCell(t);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** The ~4 km grid cell dedupeTiles collapses a tile into — hoisted out so it
 *  can double as the unit of GROUND ALREADY QUERIED. A second, supplemental
 *  corridor fetch subtracts exactly what the first one covered, using the same
 *  quantisation, so "we already have this" means the same thing in both
 *  places. Two definitions of the same grid would silently re-query or
 *  silently skip. */
function tileCell(t: Tile): string {
  const cLat = (t.south + t.north) / 2;
  const cLng = (t.west + t.east) / 2;
  return `${Math.round(cLat / 0.04)}:${Math.round(cLng / 0.04)}`;
}

/** Call our overpass edge function with a tile list. Returns merged elements.
 *
 * Long routes can produce 25+ tiles. Each Overpass query may take 10–30s, and
 * the edge function has a 150s wall-clock limit. We chunk the tiles into small
 * batches (each batch is well under the timeout) and run batches in parallel
 * from the browser. */
// Durable Overpass cache. Road / park / water data is essentially static, so we
// cache the merged result for a given (kind + quantized tile-set) and skip
// Overpass entirely on repeat queries — the same trip regenerated (every
// tolerance change / re-render), a revisited route, or an overlapping corridor.
// This is the core relief for the public-Overpass 429s: fewer live calls =
// fewer throttled failures = the pleasantness signal actually shows up.
// v3 (Morgan's blind-data log, July 15): v2 entries written during the July
// 3-14 Overpass starvation hold PARTIAL merges (batches that failed were
// silently dropped, the skimpy remainder was cached for 14 days) — so routes
// on US-9 read hwy0 from cache long after the server recovered. Bumping the
// prefix orphans every poisoned entry at once; the sweeper below deletes them.
const OVERPASS_CACHE_PREFIX = "ovp:v3:";
const OVERPASS_CACHE_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
const OVERPASS_CACHE_MAX_ELEMENTS = 6000; // don't persist giant cross-country payloads
// One-time cleanup of orphaned v2 entries (old keys are never read again;
// without this they'd sit in localStorage quota until manually cleared).
try {
  if (typeof window !== "undefined") {
    for (const k of Object.keys(window.localStorage)) {
      if (k.startsWith("ovp:v2:")) window.localStorage.removeItem(k);
    }
  }
} catch { /* best effort */ }

// RETIRED Aug 23. Keyed the cache on the WHOLE tile set, so two routes whose
// tiles differed by a single cell shared nothing — the reason one Boston search
// made ~63 tile queries for ~7 distinct cells. The per-cell key in
// fetchOneTileCell replaces it. Left here, commented, because the shape of this
// key IS the bug and deleting it silently would lose that.
//
// function overpassTilesCacheKey(kind, tiles) {
//   const cells = tiles.map((t) => `${round(midLat/0.04)}:${round(midLng/0.04)}`);
//   return `${OVERPASS_CACHE_PREFIX}${kind}:${[...new Set(cells)].sort().join("|")}`;
// }

function readOverpassCache(key: string): OverpassEl[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const { t, v } = JSON.parse(raw) as { t: number; v: OverpassEl[] };
    if (Date.now() - t > OVERPASS_CACHE_TTL_MS) { window.localStorage.removeItem(key); return null; }
    return Array.isArray(v) ? v : null;
  } catch { return null; }
}

function writeOverpassCache(key: string, value: OverpassEl[]): void {
  if (typeof window === "undefined") return;
  if (value.length === 0 || value.length > OVERPASS_CACHE_MAX_ELEMENTS) return; // don't cache empties (retry) or huge payloads
  // Per-cell keys were filling the whole localStorage quota (Sep 16 2026,
  // Sentry QuotaExceededError from the Supabase token refresh) -- see
  // storageBudget.ts. This makes room from OUR OWN keys before giving up.
  setItemWithinBudget(OVERPASS_BUDGET, key, JSON.stringify({ t: Date.now(), v: value }));
}

// ---------------------------------------------------------------------------
// PER-CELL FETCH, SHARED ACROSS ROUTES (Morgan, Aug 23).
//
// THE PROBLEM THIS FIXES, measured end to end on Boston -> Portsmouth:
// one search issued 12 tiled edge invocations carrying ~63 tile queries. Five
// candidate routes down ONE 62-mile corridor each asked for their own ~6 tiles,
// and those tiles overlap almost completely — the deduped union is about 7.
// The old cache key was the whole tile SET, so two routes whose tiles differed
// by one cell shared nothing at all.
//
// WHY THAT WAS FATAL, and it is not what I first assumed. The `environment`
// query is NOT pathological: on our private box, idle, one tile answers in
// 2.7–3.0s and returns at the `out ... 350` output cap. It is the CONCURRENCY
// that kills it. Measured ladder, same tile, same box:
//
//     1 concurrent  ->  2.8s   1/1 ok
//     2 concurrent  ->  3.4s   2/2 ok
//     4 concurrent  ->  4.3s   4/4 ok
//     8 concurrent  -> 17.8s   7/8 ok   (one call pinned at 17.8s)
//    10 × 6 tiles   -> 20.4s   0/10 ok  (every call hit the 20s deadline)
//
// Flat to 4, collapsing past 8. A real search was putting ~24 concurrent tile
// queries on the box, so every one of them blew the edge function's 8s
// per-mirror timeout, fell through three dead public mirrors, and came back
// `served=0/6 elements=0 ms=20002`. Nothing cached (empty answers are never
// banked), so the next search repeated it.
//
// THE FIX IS ARITHMETIC, NOT TUNING. Fetch ONE CELL AT A TIME, cache and
// coalesce per cell, and cap how many are in flight. Boston goes from ~63 tile
// queries to ~14 (7 cells × 2 kinds), and the second, third, fourth and fifth
// route pay nothing at all.
//
// FAITHFUL TO THE SCORER. Each caller still assembles elements from ONLY its
// own tiles, so per-route feature counts (parks, waterBodies, residentialKm …)
// are unchanged. The sharing happens strictly at the network layer, on the
// same ~4 km grid `dedupeTiles` already uses to decide two tiles are the same
// query — see its comment: "Deduping loses no coverage (overlapping tiles
// return the same elements)".
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// THE COMBINED CELL QUERY (Morgan, Aug 23) — one query per cell, complete data.
//
// A CORRECTNESS FIX WEARING A TRANSPORT FIX'S CLOTHES. `summary` and
// `environment` ran as two queries over the SAME cell, and BOTH were silently
// truncating. Measured on one Boston corridor cell:
//
//     summary      out ... 250   ->   250 elements   (the cap, exactly)
//     summary      out ... 5000  ->   481 elements   (the truth)
//     environment  out ... 350   ->   350 elements   (the cap, exactly)
//     environment  out ... 3000  -> 1,003 elements   (the truth)
//
// The scorer was being handed 52% of the summary features and 35% of the
// environment features in every tile, with Overpass's internal output order
// deciding which survived. `parks`, `waterBodies`, `residentialKm`,
// `treeLinedKm` — every one of them computed from an arbitrary slice. It never
// looked wrong because 350 reads as a count, not as a ceiling. Rule 21: a
// number that couldn't be true isn't data.
//
// The cap was never a cost control either: 2,425ms at cap 250 vs 2,265ms at cap
// 5000 on the same cell. The spatial scan is the cost; the cap only decides how
// much of the answer is thrown away.
//
// Measured union of both clause sets: 3,786ms, 1,026 elements — ONE call with
// complete data, replacing two truncated calls at ~3s each.
//
// Morgan: "I'd rather score routes using the complete available map data than
// preserve scores produced from arbitrary truncated slices."
//
// SENT THROUGH THE EDGE FUNCTION'S RAW PATH, not its tiled path, and not
// through overpassDirect. The raw path takes any Overpass QL and gives the
// private box the full request budget; overpassDirect would have applied its
// own 23,000ms-remaining rule and bypassed the edge function to public mirrors
// that have not served a single request in 24 hours.
//
// EVERY CLAUSE BELOW APPEARS IN ONE OR BOTH ORIGINAL QUERIES. Nothing is
// invented. Where the two disagreed on breadth the WIDER form wins — summary's
// `way[waterway]` over environment's river|canal, environment's `way[historic]`
// over summary's name-only form — because the narrower form was only ever a
// truncation-era economy.
// ---------------------------------------------------------------------------

/** COMPLETE-ENOUGH, not knowingly-truncated (Morgan, Aug 24).
 *
 *  Measured cap ladder on the densest Boston cell — the same cell, only the cap
 *  varied:
 *
 *      out ...  6000  ->  6,000 elements (CAPPED)   6.2s   4.14 MB
 *      out ...  8000  ->  8,000 elements (CAPPED)   6.2s   5.06 MB
 *      out ... 12000  -> 12,000 elements (CAPPED)   6.6s   6.84 MB
 *      out ... 20000  -> 17,104 elements (COMPLETE) 7.2s   8.96 MB
 *
 *  17,104 is the truth for that cell; at `out ... 350` the scorer was seeing
 *  2.0% of it. And the cap costs almost nothing server-side — 6.2s to 7.2s for
 *  nearly 3x the data — because the spatial scan is the work, not the output.
 *
 *  20,000 clears the densest cell measured with headroom. The truncation
 *  warning below still fires if anything reaches it, because a bigger number
 *  that goes quiet is exactly the 250/350 bug again. */
export const COMBINED_OUT_CAP = 20_000;

export function combinedCellQuery(t: Tile): string {
  const bb = `${t.south},${t.west},${t.north},${t.east}`;
  return `[out:json][timeout:30];
(
  way["leisure"~"^(park|garden|nature_reserve|recreation_ground|common|village_green)$"](${bb});
  relation["leisure"~"^(park|nature_reserve|recreation_ground)$"](${bb});
  way["boundary"~"^(national_park|protected_area)$"](${bb});
  relation["boundary"~"^(national_park|protected_area)$"](${bb});
  way["landuse"~"^(farmland|farmyard|meadow|orchard|vineyard|allotments|grass|forest|recreation_ground)$"](${bb});
  relation["landuse"~"^(forest|farmland|recreation_ground)$"](${bb});
  way["natural"~"^(wood|grassland|heath|scrub|fell|moor)$"](${bb});
  relation["natural"="wood"](${bb});
  way["natural"="water"](${bb});
  relation["natural"="water"](${bb});
  way["waterway"](${bb});
  way["landuse"="reservoir"](${bb});
  relation["landuse"="reservoir"](${bb});
  way["natural"="coastline"](${bb});
  node["tourism"="viewpoint"](${bb});
  node["tourism"="attraction"](${bb});
  way["tourism"="attraction"](${bb});
  node["natural"~"^(peak|hill|cliff)$"](${bb});
  way["natural"~"^(beach|cliff)$"](${bb});
  way["natural"="bay"](${bb});
  relation["natural"="bay"](${bb});
  node["historic"](${bb});
  way["historic"](${bb});
  node["amenity"="library"](${bb});
  way["amenity"="library"](${bb});
  node["building"="church"](${bb});
  way["building"="church"](${bb});
  node["amenity"="place_of_worship"](${bb});
  way["amenity"="place_of_worship"](${bb});
  way["landuse"="residential"](${bb});
  way["landuse"="industrial"](${bb});
  way["highway"~"^(motorway|trunk|primary|motorway_link|trunk_link|primary_link)$"](${bb});
  way["highway"~"^(residential|living_street|pedestrian|footway|path|cycleway|track|bridleway|steps|secondary|tertiary|secondary_link|tertiary_link)$"](${bb});
  way["highway"~"^(residential|secondary|tertiary|primary)$"]["sidewalk"="no"](${bb});
  way["sidewalk"~"^(both|left|right|yes|separate)$"](${bb});
  way["cycleway"](${bb});
  node["amenity"="parking"](${bb});
  way["amenity"="parking"](${bb});
  way["service"="parking_aisle"](${bb});
  way["noexit"="yes"](${bb});
  way["highway"="service"]["service"="driveway"](${bb});
);
out tags center geom ${COMBINED_OUT_CAP};`;
}

/** Measured knee, above. 4 is the largest value with flat latency and no
 *  failures; 8 already shows a 17.8s outlier. Deliberately conservative — the
 *  box also serves discovery, navigation and other users. */
const TILE_FETCH_CONCURRENCY = 4;

let tileSlotsInUse = 0;
const tileSlotWaiters: (() => void)[] = [];

/** Process-wide gate on concurrent Overpass tile requests. Shared across
 *  routes, kinds and searches on purpose: the limit belongs to the SERVER, so
 *  it cannot be per-caller. */
async function withTileSlot<T>(fn: () => Promise<T>): Promise<T> {
  if (tileSlotsInUse >= TILE_FETCH_CONCURRENCY) {
    await new Promise<void>((res) => tileSlotWaiters.push(res));
  }
  tileSlotsInUse++;
  try {
    return await fn();
  } finally {
    tileSlotsInUse--;
    tileSlotWaiters.shift()?.();
  }
}

/** One clock for the tile path. `performance` is absent in some test and
 *  worker contexts; every existing timing line in this file already falls
 *  back to Date.now(), this just stops that ternary being written five times. */
const perfNow = (): number =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

/** In-flight requests by `kind:cell`. Two routes asking for the same cell at
 *  the same moment — the normal case within one search — share one request
 *  rather than racing each other for a slot on the box. */
const TILE_INFLIGHT = new Map<string, Promise<OverpassEl[]>>();

// ---------------------------------------------------------------------------
// IN-MEMORY CELL MEMO (Sep 1, measured). Closes the hole the durable cache
// leaves open on purpose.
//
// writeOverpassCache refuses any cell over OVERPASS_CACHE_MAX_ELEMENTS, and it
// is right to: a 20,000-element cell is ~9 MB and would eat the whole
// localStorage quota. But TILE_INFLIGHT only coalesces requests that overlap in
// TIME, and it is deleted the moment a cell settles. So a dense cell has NO
// cache at all: every later request for it goes back to the network.
//
// WHY THAT IS NOT MERELY SLOW. The private box answers a REPEATED IDENTICAL
// query with HTTP 200 and an XML body:
//
//   Error: runtime error: open64: 0 Success /osm3s_osm_base
//          Dispatcher_Client::request_read_and_idx::duplicate_query
//
// 200, so nothing upstream reads it as a failure. The edge function calls
// res.json() on it, that throws, and it is recorded as a network error; the
// cell comes back empty, i.e. UNMEASURED. Reproduced deterministically Sep 1
// against the box, same query, no other traffic:
//
//     after 40s idle ->  200   2000 ways
//     immediately    ->  200      0        <- duplicate_query
//     again          ->  200      0
//     after 20s      ->  200      0
//
// Three DIFFERENT bboxes issued back to back all returned 2000, and the
// original answered again after 60s idle. Neither load nor a rate limit: a
// per-identical-query guard.
//
// HOW OFTEN THIS ACTUALLY BITES, STATED HONESTLY. Two candidate routes do NOT
// trigger it: dedupeTiles keeps the first raw tile rather than a grid-snapped
// one, so their bboxes -- and therefore their query text -- differ. The pair
// that CAN collide is the two enrichment calls generateRoutes makes for the
// SAME route (fetchOsmFeaturesAndPoints + fetchOsmEnvironment, same coords,
// same tiles, byte-identical queries). They run in one Promise.all and
// TILE_INFLIGHT coalesces them almost always -- measured over the Northeast A/B
// (Sep 1), the second call was degraded in 1 of 62 route-level pairs. The other
// path is a REPEATED SEARCH over the same route, where a dense cell has no
// durable entry to serve it and the repeat query is byte-identical.
//
// So this is a narrow fix for a real but infrequent failure, plus the plain
// saving of not re-fetching a 20,000-element cell. It is NOT the explanation
// for the scattered UNMEASURED cells in that A/B run -- 11 PARTIAL and 16
// UNMEASURED, all inside declared coverage -- which remains OPEN. Eight of
// those 16 are the Canada gap in PRIVATE_COVERAGE (see overpassCoverage.ts);
// six more, on ground the box demonstrably holds, are still unexplained. Do not
// let this memo be mistaken for having closed that.
//
// This memo is RAM, not localStorage: the element limit does not apply and the
// quota cannot be touched. It is bounded by age and by total retained elements
// so a long session cannot grow without limit on a phone.
//
// It also restores what the per-cell rewrite stated as its intent, in its own
// words: "the second, third, fourth and fifth route pay nothing at all."
// ---------------------------------------------------------------------------

/** Long enough to cover a search and the follow-ups a user makes from it;
 *  short enough that a phone left open for an afternoon lets it all go. */
const TILE_MEMO_TTL_MS = 15 * 60_000;
/** Total elements held across all memoised cells. ~120k elements is roughly
 *  six of the very largest cells, and well under what the tab already holds
 *  while scoring them. */
const TILE_MEMO_MAX_ELEMENTS = 120_000;

const TILE_MEMO = new Map<string, { t: number; v: OverpassEl[] }>();
let tileMemoElements = 0;

function readTileMemo(key: string): OverpassEl[] | null {
  const hit = TILE_MEMO.get(key);
  if (!hit) return null;
  if (Date.now() - hit.t > TILE_MEMO_TTL_MS) {
    TILE_MEMO.delete(key);
    tileMemoElements -= hit.v.length;
    return null;
  }
  // Map iterates in insertion order, so re-inserting on read makes the
  // eviction loop below an LRU rather than a FIFO.
  TILE_MEMO.delete(key);
  TILE_MEMO.set(key, hit);
  return hit.v;
}

function writeTileMemo(key: string, value: OverpassEl[]): void {
  // An empty answer is UNMEASURED, never banked — rule 20, same as the durable
  // cache. Banking it here would be the July starvation bug in RAM.
  if (value.length === 0) return;
  const prev = TILE_MEMO.get(key);
  if (prev) tileMemoElements -= prev.v.length;
  TILE_MEMO.set(key, { t: Date.now(), v: value });
  tileMemoElements += value.length;
  // Evict least-recently-used until under the ceiling. Never evicts the entry
  // just written: one legitimate cell can be 20,000 elements on its own, and
  // dropping it immediately would make this a no-op exactly where it is needed.
  for (const [k, e] of TILE_MEMO) {
    if (tileMemoElements <= TILE_MEMO_MAX_ELEMENTS) break;
    if (k === key) continue;
    TILE_MEMO.delete(k);
    tileMemoElements -= e.v.length;
  }
}

/** Drop every memoised cell. For tests that count network calls, and for any
 *  caller that needs a genuinely cold read. */
export function resetTileMemo(): void {
  TILE_MEMO.clear();
  tileMemoElements = 0;
}

/** How many cells and elements the memo is holding. Diagnostics only. */
export function tileMemoStats(): { cells: number; elements: number } {
  return { cells: TILE_MEMO.size, elements: tileMemoElements };
}

/** Elements for ONE tile cell: durable cache, then in-flight coalescing, then
 *  the network behind the concurrency gate. Never throws — a failed cell is an
 *  empty cell, which the callers already treat as UNMEASURED (rule 20). */
function fetchOneTileCell(
  tile: Tile,
  kind: "combined" | "summary" | "environment" | "calmcorridor" | "scenic" | "waterside",
  /** performance.now() reading after which this cell gives up and reports
   *  UNMEASURED. Set ONLY for ground outside the private box's declared
   *  coverage — see overpassCoverage.ts. Undefined in coverage, where the box
   *  answers in ~1s and a client-side abort would kill calls that were about
   *  to succeed (the Aug 9 lesson, recorded in overpassDirect above). */
  deadlineAt?: number,
): Promise<OverpassEl[]> {
  const key = `${kind}:${tileCell(tile)}`;
  const cacheKey = `${OVERPASS_CACHE_PREFIX}cell:${key}`;
  // Memo first: it holds the dense cells the durable cache is not allowed to,
  // and those are the ones a repeat request gets rejected for.
  const memo = readTileMemo(key);
  if (memo) return Promise.resolve(memo);
  const cached = readOverpassCache(cacheKey);
  if (cached) return Promise.resolve(cached);
  const inflight = TILE_INFLIGHT.get(key);
  if (inflight) return inflight;

  const p = withTileSlot(async () => {
    try {
      // THE BUDGET IS ALREADY SPENT. An earlier cell on this same out-of-
      // coverage ground used the whole window, so this one would only repeat
      // its wait. Returning [] here is UNMEASURED (rule 20), not "empty".
      // This is the branch that stops eight tiles becoming eight timeouts.
      if (deadlineAt !== undefined && perfNow() >= deadlineAt) {
        // eslint-disable-next-line no-console
        console.log(
          `[overpass-tiles] cell ${tileCell(tile)} (${kind}) NOT ATTEMPTED — ` +
          `out-of-coverage budget already spent by earlier cells. UNMEASURED.`,
        );
        return [] as OverpassEl[];
      }
      // `combined` goes through the edge function's RAW path so the private box
      // gets the full request budget; every other kind keeps the tiled path.
      const body = kind === "combined"
        ? { query: combinedCellQuery(tile), label: "combined-cell" }
        : { tiles: [tile], kind };
      const call = supabase.functions.invoke("overpass", { body });
      // Out of coverage the edge function has no fast box to ask and grinds to
      // its own 20s REQUEST_DEADLINE_MS against throttled public mirrors. This
      // race is the only thing between that and the caller. In coverage there
      // is no race at all — `call` is awaited exactly as it always was.
      const { data, error } = deadlineAt === undefined
        ? await call
        : await Promise.race([
            call,
            new Promise<{ data: null; error: Error }>((resolve) =>
              setTimeout(
                () => resolve({ data: null, error: new Error("out-of-coverage budget") }),
                Math.max(0, deadlineAt - perfNow()),
              ),
            ),
          ]);
      if (error) return [] as OverpassEl[];
      const els = Array.isArray(data?.elements) ? (data.elements as OverpassEl[]) : [];
      // TRUNCATION IS NOT COMPLETENESS (Morgan, Aug 23). `out ... N` stops at N
      // and says nothing about it — which is exactly how 250 and 350 passed as
      // measurements for months. A cell that comes back holding EXACTLY its cap
      // has almost certainly been cut off. Say so, loudly, rather than letting a
      // bigger number recreate the same silence.
      if (kind === "combined" && els.length >= COMBINED_OUT_CAP) {
        // eslint-disable-next-line no-console
        console.warn(
          `[overpass-tiles] TRUNCATED cell ${tileCell(tile)} returned exactly ` +
          `${els.length} elements at the ${COMBINED_OUT_CAP} cap — POTENTIALLY ` +
          `INCOMPLETE, this cell's features are a slice, not a measurement. ` +
          `Raise COMBINED_OUT_CAP.`,
        );
      }
      // Only bank a NON-empty answer. An empty one is a failed upstream, and
      // caching it poisons the area for the whole TTL — the July starvation
      // bug, learned the hard way.
      //
      // AND NEVER BANK A PARTIAL (Morgan, Aug 24: "If a response is too large
      // for durable browser cache, skip persistence for that cell rather than
      // caching incomplete data"). A dense cell at the new cap is ~9 MB, over
      // OVERPASS_CACHE_MAX_ELEMENTS and near the whole localStorage quota.
      // writeOverpassCache already refuses those — the point of this branch is
      // that the refusal SAYS SO, instead of looking like a cache that works.
      if (els.length > 0) {
        if (els.length > OVERPASS_CACHE_MAX_ELEMENTS) {
          // eslint-disable-next-line no-console
          console.log(
            `[overpass-tiles] cell ${tileCell(tile)} NOT PERSISTED — ${els.length} ` +
            `elements exceeds the ${OVERPASS_CACHE_MAX_ELEMENTS} durable-cache limit. ` +
            `Held in memory for this session instead (see TILE_MEMO); a later ` +
            `session re-fetches it.`,
          );
        }
        // Memo ALWAYS, cache only if it fits. This is the whole fix: the cells
        // the durable cache refuses are precisely the ones a second candidate
        // would otherwise re-request and be answered `duplicate_query`.
        writeTileMemo(key, els);
        writeOverpassCache(cacheKey, els);
      }
      return els;
    } catch {
      return [] as OverpassEl[];
    }
  }).finally(() => {
    // Drop it either way: a failed cell must be retryable by the next search.
    TILE_INFLIGHT.delete(key);
  });
  TILE_INFLIGHT.set(key, p);
  return p;
}

async function fetchOverpassTiles(
  tiles: { south: number; west: number; north: number; east: number }[],
  kind: "combined" | "summary" | "environment" | "calmcorridor" | "scenic" | "waterside",
): Promise<OverpassEl[]> {
  if (tiles.length === 0) return [];
  // Hard cap so a runaway caller can't spawn hundreds of cell queries — the
  // same 80 the edge function enforces on its side.
  const cells = dedupeTiles(tiles).slice(0, 80);
  const t0 = perfNow();

  // COVERAGE GATE (Aug 31, Waco). Cells outside the private box's declared
  // extract get ONE shared budget, not one wait each — see overpassCoverage.ts
  // for the measurement that forced this. Per cell, because a long corridor is
  // genuinely mixed: Toronto->Maine crosses the boundary, and the cells that
  // ARE in coverage must keep the untimed path that serves them in ~1s.
  const outOfCoverage = cells.filter((t) => !tileInPrivateCoverage(t)).length;
  const budgetEndsAt = outOfCoverage > 0 ? t0 + OUT_OF_COVERAGE_BUDGET_MS : undefined;

  // Count what each cell cost us BEFORE issuing anything, so the confession
  // below can separate "we already had it" from "we asked again".
  let fromCache = 0, coalesced = 0;
  for (const t of cells) {
    const key = `${kind}:${tileCell(t)}`;
    if (readOverpassCache(`${OVERPASS_CACHE_PREFIX}cell:${key}`)) fromCache++;
    else if (TILE_INFLIGHT.has(key)) coalesced++;
  }
  const fetched = cells.length - fromCache - coalesced;

  const perCell = await Promise.all(cells.map((t) =>
    fetchOneTileCell(t, kind, tileInPrivateCoverage(t) ? undefined : budgetEndsAt),
  ));

  // Merge + dedupe by element id across cells.
  const seen = new Set<string>();
  const merged: OverpassEl[] = [];
  for (const arr of perCell) {
    for (const el of arr) {
      const key = `${el.type}:${el.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(el);
    }
  }

  // NO SILENT VERDICTS (rule 17). The whole point of this change is how many
  // network queries a search makes; that number has to be visible or the next
  // regression is invisible again. `served` names how many cells came back
  // with anything — `served=0` means UNMEASURED, not "nothing here".
  const served = perCell.filter((a) => a.length > 0).length;
  // WHERE runtime data is weak, not just THAT it is (Morgan, Aug 24, item 6).
  // The private box holds the us-northeast extract only; everywhere else falls
  // through to public mirrors that have not served a request in 24 hours. A
  // search in Denver and a search in Boston fail differently and we could not
  // previously tell them apart in a log. The centre of the queried ground goes
  // in the line so degradation can be read geographically.
  const mid = cells.length > 0
    ? {
        lat: cells.reduce((a, t) => a + (t.south + t.north) / 2, 0) / cells.length,
        lng: cells.reduce((a, t) => a + (t.west + t.east) / 2, 0) / cells.length,
      }
    : { lat: 0, lng: 0 };
  const coverage = served === 0 ? "UNMEASURED"
                 : served < cells.length ? "PARTIAL"
                 : "COMPLETE";
  // Same numbers as the console line below, made countable off-device. Purely
  // additive: no branch, no await, no effect on what this function returns.
  noteOverpassCells(cells.length, served);
  // eslint-disable-next-line no-console
  console.log(
    `[overpass-tiles] kind=${kind} cells=${cells.length} ` +
    `(cached=${fromCache} coalesced=${coalesced} fetched=${fetched}) ` +
    `served=${served}/${cells.length} elements=${merged.length} ` +
    `coverage=${coverage} outside-extract=${outOfCoverage}/${cells.length} ` +
    `at=${mid.lat.toFixed(2)},${mid.lng.toFixed(2)} ` +
    `ms=${Math.round(perfNow() - t0)}`,
  );
  if (served === 0 && cells.length > 0) {
    // NOT "no features here" — "we could not look here" (rule 20). Warned, not
    // logged, so it is countable in Sentry and separable by geography.
    // eslint-disable-next-line no-console
    console.warn(
      `[enrich-coverage] UNMEASURED at ${mid.lat.toFixed(3)},${mid.lng.toFixed(3)} — ` +
      `${cells.length} cell(s) of ${kind} returned nothing. Scores here rest on ` +
      `router metadata only; this is a data gap, not a quiet area.` +
      (outOfCoverage === cells.length
        ? ` ALL of this ground is outside the private box's extract, so only the ` +
          `throttled public mirrors could have answered — capped at ` +
          `${OUT_OF_COVERAGE_BUDGET_MS}ms rather than waiting out the edge ` +
          `function's 20s deadline per cell.`
        : outOfCoverage > 0
          ? ` ${outOfCoverage} of ${cells.length} cell(s) are outside the private ` +
            `box's extract and were capped at ${OUT_OF_COVERAGE_BUDGET_MS}ms.`
          : ` This ground IS inside the declared extract — the box itself is the ` +
            `suspect, not coverage.`),
    );
  }
  return merged;
}

/**
 * OpenStreetMap Overpass API — counts parks, water, residential roads, bike lanes
 * within the bounding box of a route. Free, no API key.
 */
export interface OsmFeatures {
  parks: number;
  waterBodies: number;
  residentialKm: number;
  highwayKm: number;
  /** Major non-highway roads (secondary + tertiary) — busy arterials like
   *  N Broadway / US-9 that aren't motorways but are unpleasant on foot. */
  majorRoadKm: number;
  bikeLaneKm: number;
  /** Subset of bikeLaneKm: physically-separated infrastructure (dedicated
   *  cycleways, tracks, off-street paths). This is the gold-standard biking
   *  surface — vastly nicer to ride than paint-only. Optional for backward
   *  compatibility with callers that don't populate it. */
  protectedBikeKm?: number;
  /** Subset of bikeLaneKm: painted-only lanes (cycleway=lane / shared lane).
   *  Better than no infrastructure but offers no physical protection.
   *  Optional for backward compatibility. */
  paintedBikeKm?: number;
  /** Km of cycleway/bike-friendly path with high-quality smooth surface
   *  (asphalt/paved/concrete + smoothness good/excellent/intermediate).
   *  A small bonus for biking — rough cobbles or potholes ruin a ride.
   *  Optional for backward compatibility. */
  smoothBikeKm?: number;
  /** Walk-friendly km: footway/path/pedestrian + roads with sidewalk≠no. */
  sidewalkKm: number;
  /** Km of dedicated standalone foot infrastructure (footway/path/pedestrian). */
  footwayKm: number;
  /** Roads explicitly tagged sidewalk=no — penalty for walking. */
  noSidewalkKm: number;
  /** Count of historic-district / historic-quarter polygons whose centroid
   *  falls inside the route corridor. Used as a small lever bonus on
   *  Pleasant + Scenic (#104). Optional for backward compatibility. */
  historicDistrictCount?: number;
  /** Km of route corridor alongside OSM tree-lined features:
   *  natural=tree_row (streets explicitly tagged as tree-lined),
   *  natural=wood and landuse=forest (forested areas). This is the
   *  free OSM proxy for "tree canopy" — less complete than per-city
   *  LiDAR datasets but globally consistent. Used as a small lever
   *  on Pleasant + Scenic (#106). Optional for backward compatibility. */
  treeLinedKm?: number;
  /** Km of route corridor tagged as a scenic byway / official scenic
   *  route. OSM tags: scenic=yes, byway=*, tourism=route with
   *  scenic-byway-style markings. Driving signal — Blue Ridge Parkway,
   *  Mohawk Trail, state scenic byways (#108). */
  scenicBywayKm?: number;
  /** Km of route on slow streets — maxspeed ≤ 30 mph or residential.
   *  Pleasant driving signal: a slow road feels qualitatively nicer
   *  than a 45mph stroad even when both have similar tags (#108). */
  slowStreetKm?: number;
  /** Km of route on fast arterials — maxspeed ≥ 40 mph. Driving
   *  penalty signal beyond the existing highway/major-road tracking;
   *  catches 45mph stroads that aren't strictly highways but feel
   *  hostile (#108). */
  fastArterialKm?: number;
  /** Km of route on wide multi-lane roads — OSM `lanes` >= 4 (i.e. a
   *  4+-lane road, typically 2+ each way). Driving penalty signal: a
   *  4-lane road is a wide, unpleasant "stroad" even when its maxspeed
   *  isn't tagged fast. Catches big arterials the speed/highway tags miss
   *  (Morgan: penalize 4-lane roads harder, esp. on short drives). */
  fourLaneKm?: number;
  /** Km of route along ways tagged with street lighting
   *  (`lit=yes`, `lit=24/7`, `lit=automatic`, etc.). Night-time
   *  walking + biking comfort signal (#114). Optional. */
  litStreetKm?: number;
}

/** Per-feature points for accurate per-segment scoring. */
export interface OsmFeaturePoints {
  parks: { lng: number; lat: number; name?: string }[];
  water: { lng: number; lat: number; name?: string }[];
  historic: { lng: number; lat: number; name?: string }[];
}

/**
 * Rich per-tag environmental data for the segment scorer.
 *
 * Why points + lines:
 *   - Points (centroids) are fine for parks, gardens, attractions, churches,
 *     libraries, and historic features.
 *   - Lines (polylines from way geometries) are critical for roads, rivers,
 *     canals, and the coastline — a route segment runs *next to* or *on* a
 *     line, so a single centroid would miss most hits or assign them to the
 *     wrong segment.
 *
 * Each entry carries a `kind` so segment scoring can apply the +/- weights
 * defined in the spec (park/garden +4, water/coast +5, motorway -6, etc.)
 * and per-profile multipliers (Nature, Water, Historic, Quiet, Lively).
 */
export type OsmAreaKind =
  // Strong positive — green/open spaces
  | "park"            // leisure=park
  | "garden"          // leisure=garden
  | "nature_reserve"  // leisure=nature_reserve / boundary=national_park / protected_area
  | "recreation_ground" // leisure/landuse=recreation_ground, common, village_green
  | "farmland"        // landuse=farmland / farmyard
  | "meadow"          // landuse=meadow / natural=grassland
  | "orchard"         // landuse=orchard / vineyard / allotments
  | "open_green"      // landuse=grass, natural=heath/scrub/fell/moor
  | "viewpoint"       // tourism=viewpoint / natural=peak/hill
  | "water"           // natural=water
  | "river"           // waterway=river
  | "canal"           // waterway=canal
  | "coastline"       // natural=coastline
  | "wood"            // natural=wood
  | "forest"          // landuse=forest
  | "historic"        // historic=*
  | "attraction"      // tourism=attraction
  // Moderate positive
  | "residential_land"// landuse=residential
  | "residential"     // highway=residential
  | "living_street"   // highway=living_street
  | "pedestrian"      // highway=pedestrian
  | "path"            // highway=path
  | "cycleway"        // highway=cycleway
  | "secondary"       // highway=secondary — busy suburban/city arterial
  | "tertiary"        // highway=tertiary — busy collector road
  | "secondary_link"  // highway=secondary_link
  | "tertiary_link"   // highway=tertiary_link
  | "no_sidewalk"     // road explicitly tagged sidewalk=no
  | "library"         // amenity=library
  | "church"          // building=church
  // Negative
  | "industrial"      // landuse=industrial
  | "motorway"        // highway=motorway
  | "trunk"           // highway=trunk
  | "primary"         // highway=primary
  | "motorway_link"   // highway=motorway_link
  | "trunk_link"      // highway=trunk_link
  | "primary_link"    // highway=primary_link
  | "parking"         // amenity=parking
  | "parking_aisle"   // service=parking_aisle
  // (#127) Dead-end / no-thru-traffic ways. Tagged in OSM as either
  // `noexit=yes` on the way itself OR `highway=service` + `service=driveway`
  // (cul-de-sac connector pattern). Used to drop route candidates that
  // detour into a dead-end street and have to back out, unless the
  // dead-end way contains the trip's start or end address.
  | "dead_end";       // noexit=yes OR (highway=service AND service=driveway)

export interface OsmPointFeature {
  kind: OsmAreaKind;
  lng: number;
  lat: number;
  name?: string;
}
export interface OsmLineFeature {
  kind: OsmAreaKind;
  /** Way polyline as [lng, lat] pairs. Used for distance-to-segment math. */
  line: LngLat[];
  name?: string;
}

export interface OsmEnvironment {
  /** Centroid features for area-like / point-like tags (parks, water bodies, historic, etc.). */
  points: OsmPointFeature[];
  /** Way polylines for linear features (roads, rivers, coastline, canal). */
  lines: OsmLineFeature[];
}



/** Pick a representative point for an Overpass element (geom centroid or center). */
function pickPoint(el: OverpassEl): { lng: number; lat: number } | null {
  if (typeof el.lat === "number" && typeof el.lon === "number") {
    return { lng: el.lon, lat: el.lat };
  }
  if (el.center && typeof el.center.lat === "number") {
    return { lng: el.center.lon, lat: el.center.lat };
  }
  if (Array.isArray(el.geometry) && el.geometry.length > 0) {
    let sx = 0, sy = 0;
    for (const g of el.geometry) { sx += g.lon; sy += g.lat; }
    return { lng: sx / el.geometry.length, lat: sy / el.geometry.length };
  }
  return null;
}

function pickLine(el: OverpassEl): LngLat[] | null {
  if (Array.isArray(el.geometry) && el.geometry.length >= 2) {
    return el.geometry.map((g): LngLat => [g.lon, g.lat]);
  }
  return null;
}

/** Sample up to ~12 evenly spaced points along an Overpass element's
 *  geometry (typically the perimeter of an area like a park or forest),
 *  plus the centroid. Lets the route-proximity test match the boundary
 *  of huge polygons rather than only their centers. */
function sampleAreaPoints(el: OverpassEl): { lng: number; lat: number }[] {
  const out: { lng: number; lat: number }[] = [];
  const centroid = pickPoint(el);
  if (centroid) out.push(centroid);
  const geom = Array.isArray(el.geometry) ? el.geometry : [];
  if (geom.length >= 2) {
    const stride = Math.max(1, Math.floor(geom.length / 12));
    for (let i = 0; i < geom.length; i += stride) {
      const g = geom[i];
      if (typeof g?.lon === "number" && typeof g?.lat === "number") {
        out.push({ lng: g.lon, lat: g.lat });
      }
    }
  }
  return out;
}

/**
 * Fetch BOTH aggregate route features AND per-feature points (parks / water /
 * historic) so the segment scorer can attribute hits to specific segments
 * instead of using a corridor-wide density estimate.
 */
export async function fetchOsmFeaturesAndPoints(
  coords: LngLat[],
): Promise<{ features: OsmFeatures; points: OsmFeaturePoints }> {
  const empty = {
    features: {
      parks: 0, waterBodies: 0, residentialKm: 0, highwayKm: 0,
      majorRoadKm: 0, bikeLaneKm: 0, protectedBikeKm: 0,
      paintedBikeKm: 0, smoothBikeKm: 0,
      sidewalkKm: 0, footwayKm: 0, noSidewalkKm: 0,
    } as OsmFeatures,
    points: { parks: [], water: [], historic: [] } as OsmFeaturePoints,
  };
  if (coords.length < 2) return empty;

  // Tile the route — short routes get one tile, long routes get many small ones.
  const tiles = routeTiles(coords, 12);
  const r = (n: number) => Math.round(n * 1000) / 1000;

  // Cache key over the whole-route bbox so we don't re-tile when nothing changed.
  const whole = bbox(coords, 0.005);
  const cacheKey = `osm2:v6:${tiles.length}:${r(whole.minLat)}_${r(whole.minLng)}_${r(whole.maxLat)}_${r(whole.maxLng)}`;
  const cached = readCache<{ features: OsmFeatures; points: OsmFeaturePoints }>(cacheKey);
  if (cached) return cached;

  // One round-trip to the edge function — it fans out to Overpass server-side.
  const elements = await fetchOverpassTiles(tiles, "combined");

  let parks = 0, water = 0, resKm = 0, hwyKm = 0, majorKm = 0, bikeKm = 0;
  let protectedBikeKm = 0, paintedBikeKm = 0, smoothBikeKm = 0;
  let footKm = 0, sideKm = 0, noSideKm = 0;
  let historicDistrictCount = 0;
  let treeLinedKm = 0;
  let scenicBywayKm = 0;
  let slowStreetKm = 0;
  let fastArterialKm = 0;
  let fourLaneKm = 0;
  let litStreetKm = 0;
  const parkPts: OsmFeaturePoints["parks"] = [];
  const waterPts: OsmFeaturePoints["water"] = [];
  const histPts: OsmFeaturePoints["historic"] = [];

  for (const el of elements) {
    const t = el.tags ?? {};
    const geomKm = wayLengthKm(el.geometry ?? []);
    // Treat all open-green tags (parks, nature reserves, farmland, meadow,
    // forest, orchards, etc.) as "parks" for the summary count + park points.
    const isGreen =
      t.leisure === "park" ||
      t.leisure === "garden" ||
      t.leisure === "nature_reserve" ||
      t.leisure === "recreation_ground" ||
      t.leisure === "common" ||
      t.leisure === "village_green" ||
      t.boundary === "national_park" ||
      t.boundary === "protected_area" ||
      ["farmland","farmyard","meadow","orchard","vineyard","allotments","grass","forest","recreation_ground"].includes(t.landuse) ||
      ["wood","grassland","heath","scrub","fell","moor"].includes(t.natural);
    if (isGreen) {
      parks++;
      const p = pickPoint(el); if (p) parkPts.push({ ...p, name: t.name });
    }
    if (
      t.natural === "water" ||
      t.waterway ||
      t.landuse === "reservoir" ||
      t.water === "reservoir" ||
      t.water === "lake"
    ) {
      water++;
      const p = pickPoint(el); if (p) waterPts.push({ ...p, name: t.name });
    }
    if (t.historic) {
      const p = pickPoint(el); if (p) histPts.push({ ...p, name: t.name });
    }
    // Historic district overlay (#104). Small lever for "this whole
    // neighborhood is pretty" — Boston Back Bay, Charleston, Savannah,
    // Santa Fe historic core, Greenwich Village historic district, etc.
    // Different from individual historic POIs (monuments, museums) which
    // are already in histPts above — districts cover blocks of character.
    if (
      t.historic === "district" ||
      t.historic === "heritage" ||
      t.boundary === "historic" ||
      t.place === "historic_quarter"
    ) {
      historicDistrictCount++;
    }
    // Tree-lined km (#106). Three OSM tags, all proxies for shade /
    // canopy along the route:
    //   • natural=tree_row — explicit "this street is tree-lined"
    //   • natural=wood     — wooded patch (parks count here too)
    //   • landuse=forest   — managed forest land
    // Geometry-aware: way length (geomKm) is the distance the route
    // spends adjacent to the feature, not the feature's full extent.
    if (
      t.natural === "tree_row" ||
      t.natural === "wood" ||
      t.landuse === "forest"
    ) {
      treeLinedKm += geomKm;
    }
    // Scenic byway designation (#108). Several OSM tagging conventions
    // exist for "this road is officially scenic" — accept any of them.
    if (
      t.scenic === "yes" ||
      t.byway === "yes" ||
      t.byway === "scenic" ||
      (typeof t.byway === "string" && t.byway.length > 0) ||
      t.designation === "scenic_byway" ||
      t.scenic_byway === "yes" ||
      (t.tourism === "route" && t.route !== "bicycle" && t.route !== "foot")
    ) {
      scenicBywayKm += geomKm;
    }
    // Speed-limit driving feel (#108). Parse maxspeed values like
    // "25 mph" / "30" / "50 km/h". Slow streets earn a pleasant
    // bonus; fast arterials earn an extra penalty beyond the
    // existing highway tags.
    if (typeof t.highway === "string" && typeof t.maxspeed === "string") {
      const mph = parseMaxspeedMph(t.maxspeed);
      if (mph != null) {
        if (mph <= 30) slowStreetKm += geomKm;
        else if (mph >= 40) fastArterialKm += geomKm;
      } else if (t.highway === "residential" || t.highway === "living_street") {
        // No explicit speed limit but residential = slow by convention.
        slowStreetKm += geomKm;
      }
    } else if (t.highway === "residential" || t.highway === "living_street") {
      slowStreetKm += geomKm;
    }
    // Wide multi-lane roads (Morgan: penalize 4-lane roads). OSM `lanes`
    // is the TOTAL lane count (both directions), so lanes >= 4 = a road
    // with ~2+ lanes each way — the wide, hostile arterial we want to
    // avoid on a pleasant drive. Only count actual driveable roads, and
    // skip links/ramps (short connectors shouldn't read as a 4-lane road).
    if (
      typeof t.highway === "string" &&
      t.highway !== "footway" && t.highway !== "path" &&
      t.highway !== "cycleway" && t.highway !== "pedestrian" &&
      !t.highway.endsWith("_link") &&
      typeof t.lanes === "string"
    ) {
      const ln = parseInt(t.lanes, 10);
      if (Number.isFinite(ln) && ln >= 4) fourLaneKm += geomKm;
    }
    // Street lighting (#114). lit=yes/24/7/automatic = well-lit at
    // night. Tracked for night walking + biking comfort. We accept
    // any non-"no" lit value as positive evidence.
    if (
      typeof t.highway === "string" &&
      typeof t.lit === "string" &&
      t.lit !== "no" &&
      t.lit !== "disused"
    ) {
      litStreetKm += geomKm;
    }
    if (t.highway === "residential") resKm += geomKm;
    if (["motorway", "trunk", "primary"].includes(t.highway)) hwyKm += geomKm;
    // Secondary/tertiary = "major road but not a freeway". On foot, these
    // are the loud, 4-lane arterials (N Broadway, Geary, Western Ave) that
    // ruin a walk even though they technically aren't highways.
    if (["secondary", "tertiary", "secondary_link", "tertiary_link"].includes(t.highway)) {
      majorKm += geomKm;
    }
    // Bike infrastructure classification (#101). Three grades:
    //   • protected — physically separated (dedicated cycleway, track,
    //     off-street path, separated lane). Gold standard.
    //   • painted   — painted-only lane (no physical barrier).
    //   • bike-friendly path — bicycle=designated on a shared path.
    // All count toward bikeLaneKm so the existing scoring keeps working,
    // and the new protected/painted subsets enable a stronger boost for
    // physically-separated infrastructure.
    const cwTags = [t.cycleway, t["cycleway:left"], t["cycleway:right"], t["cycleway:both"]];
    const isProtectedCycleway =
      t.highway === "cycleway" ||
      cwTags.some((v) => v === "track" || v === "separated" || v === "opposite_track") ||
      (t.bicycle === "designated" &&
        ["path", "footway", "pedestrian", "track"].includes(t.highway));
    const isPaintedCycleway =
      !isProtectedCycleway &&
      cwTags.some(
        (v) =>
          v === "lane" ||
          v === "opposite_lane" ||
          v === "shared_lane" ||
          v === "shoulder",
      );
    if (isProtectedCycleway) {
      protectedBikeKm += geomKm;
      bikeKm += geomKm;
    } else if (isPaintedCycleway) {
      paintedBikeKm += geomKm;
      bikeKm += geomKm;
    } else if (t.cycleway) {
      // Catch-all for cycleway values we don't classify (no/separate/etc).
      // Still counts as bike infra but doesn't earn the protected bonus.
      bikeKm += geomKm;
    }
    // Surface quality — a small bonus on biking when the cycleway/path
    // has a smooth, predictable surface. Cobblestones and unpaved
    // surfaces are fine to recognize but don't earn the smooth bonus.
    const isSmoothSurface =
      ["asphalt", "paved", "concrete", "concrete:plates", "concrete:lanes"].includes(t.surface) ||
      ["excellent", "good", "intermediate"].includes(t.smoothness);
    if ((isProtectedCycleway || isPaintedCycleway) && isSmoothSurface) {
      smoothBikeKm += geomKm;
    }
    // Walking infra: standalone foot-friendly ways.
    if (["footway", "path", "pedestrian", "living_street", "track", "bridleway", "steps"].includes(t.highway)) {
      footKm += geomKm;
      sideKm += geomKm;
    }
    // Roads with a sidewalk tag — counts as walk-friendly km.
    if (typeof t.sidewalk === "string" && /^(both|left|right|yes|separate)$/.test(t.sidewalk)) {
      sideKm += geomKm;
    }
    // Explicit "no sidewalk" — penalty for walking.
    if (t.sidewalk === "no" && ["residential", "tertiary", "secondary"].includes(t.highway)) {
      noSideKm += geomKm;
    }
  }

  const result = {
    features: {
      parks, waterBodies: water,
      residentialKm: resKm, highwayKm: hwyKm, majorRoadKm: majorKm, bikeLaneKm: bikeKm,
      protectedBikeKm, paintedBikeKm, smoothBikeKm,
      sidewalkKm: sideKm, footwayKm: footKm, noSidewalkKm: noSideKm,
      historicDistrictCount,
      treeLinedKm,
      scenicBywayKm,
      slowStreetKm,
      fastArterialKm,
      fourLaneKm,
      litStreetKm,
    },
    points: { parks: parkPts, water: waterPts, historic: histPts },
  };
  // AN EMPTY ANSWER IS UNMEASURED, NOT "NOTHING HERE" (rule 20), AND MUST NOT
  // BE REMEMBERED (Morgan, Aug 23). The per-cell layer already refuses to bank
  // empties — the July starvation lesson — but this OUTER cache was writing
  // `{points:[],lines:[]}` for the full TTL whenever upstream failed, which
  // negated it completely: one bad minute made the corridor score on nothing
  // for 48 hours, and every later search read the zero back as a measurement.
  if (elements.length > 0) writeCache(cacheKey, result);
  return result;
}

/** Backward-compatible aggregates-only call. */
export async function fetchOsmFeatures(coords: LngLat[]): Promise<OsmFeatures> {
  return (await fetchOsmFeaturesAndPoints(coords)).features;
}

/** Parse an OSM `maxspeed` string into mph. Returns null when the
 *  string can't be interpreted. Examples handled:
 *    "25 mph"    → 25
 *    "30"        → 30 (assumed mph in US — OSM convention)
 *    "50 km/h"   → 31 (converted)
 *    "RU:urban"  → null (named limit, varies)
 *    "signals"   → null (variable speed sign)
 *  Exported so it can be unit-tested. */
export function parseMaxspeedMph(raw: string): number | null {
  const trimmed = raw.trim().toLowerCase();
  const m = trimmed.match(/^(\d+(?:\.\d+)?)\s*(mph|km\/h|kmh|knots)?$/);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (!isFinite(n) || n <= 0) return null;
  const unit = m[2];
  if (!unit || unit === "mph") return n;
  if (unit === "km/h" || unit === "kmh") return n * 0.621371;
  if (unit === "knots") return n * 1.15078;
  return null;
}

function wayLengthKm(geom: { lat: number; lon: number }[]): number {
  if (geom.length < 2) return 0;
  const R = 6371;
  let total = 0;
  for (let i = 1; i < geom.length; i++) {
    const a = geom[i - 1], b = geom[i];
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lon - a.lon) * Math.PI) / 180;
    const lat1 = (a.lat * Math.PI) / 180;
    const lat2 = (b.lat * Math.PI) / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
    total += 2 * R * Math.asin(Math.sqrt(x));
  }
  return total;
}

// =====================================================================
// Rich environment fetch — drives the OSM-based pleasantness scorer.
// =====================================================================

/**
 * Single Overpass query that captures every tag in the spec:
 * strong positives (park/garden/water/river/canal/coast/wood/forest/historic/
 * attraction), moderate positives (residential land + streets, living/
 * pedestrian/path/cycleway, library, church), and negatives (industrial,
 * motorway/trunk/primary + their _link variants, parking, parking aisles).
 *
 * Returns POINTS for area-like + point-like tags and LINES for the linear
 * ones (roads, rivers, canal, coastline) so segment scoring can do real
 * distance-to-line proximity. Cached 48 h per bbox.
 */
export async function fetchOsmEnvironment(coords: LngLat[]): Promise<OsmEnvironment> {
  if (coords.length < 2) return { points: [], lines: [] };

  const tiles = routeTiles(coords, 12);
  const r = (n: number) => Math.round(n * 1000) / 1000;
  const whole = bbox(coords, 0.005);
  const cacheKey = `osm-env:v6:${tiles.length}:${r(whole.minLat)}_${r(whole.minLng)}_${r(whole.maxLat)}_${r(whole.maxLng)}`;
  const cached = readCache<OsmEnvironment>(cacheKey);
  if (cached) return cached;

  const elements = await fetchOverpassTiles(tiles, "combined");
  const result = buildEnvironment(elements);
  // AN EMPTY ANSWER IS UNMEASURED, NOT "NOTHING HERE" (rule 20), AND MUST NOT
  // BE REMEMBERED (Morgan, Aug 23). The per-cell layer already refuses to bank
  // empties — the July starvation lesson — but this OUTER cache was writing
  // `{points:[],lines:[]}` for the full TTL whenever upstream failed, which
  // negated it completely: one bad minute made the corridor score on nothing
  // for 48 hours, and every later search read the zero back as a measurement.
  if (elements.length > 0) writeCache(cacheKey, result);
  return result;
}

/** Multi-route corridor environment. Tiles each route SEPARATELY (never
 *  concatenated — concatenation creates a giant bogus tile spanning the gap
 *  from one route's end back to the next's start) then dedupes overlapping
 *  tiles so the shared corridor is queried once, not once per candidate.
 *  Replaces the old `fetchOsmEnvironment(raw.flatMap(r => r.coordinates))`
 *  call that drove route generation to several minutes. */
export async function fetchOsmEnvironmentForRoutes(
  routes: LngLat[][],
): Promise<OsmEnvironment> {
  const valid = routes.filter((c) => c.length >= 2);
  if (valid.length === 0) return { points: [], lines: [] };
  const tiles = dedupeTiles(valid.flatMap((c) => routeTiles(c, 12)));
  if (tiles.length === 0) return { points: [], lines: [] };
  const r = (n: number) => Math.round(n * 1000) / 1000;
  const sig = tiles
    .map((t) => `${r(t.south)}_${r(t.west)}`)
    .sort()
    .join("|");
  const cacheKey = `osm-env-multi:v1:${tiles.length}:${sig}`;
  const cached = readCache<OsmEnvironment>(cacheKey);
  if (cached) return cached;
  const elements = await fetchOverpassTiles(tiles, "combined");
  const result = buildEnvironment(elements);
  // AN EMPTY ANSWER IS UNMEASURED, NOT "NOTHING HERE" (rule 20), AND MUST NOT
  // BE REMEMBERED (Morgan, Aug 23). The per-cell layer already refuses to bank
  // empties — the July starvation lesson — but this OUTER cache was writing
  // `{points:[],lines:[]}` for the full TTL whenever upstream failed, which
  // negated it completely: one bad minute made the corridor score on nothing
  // for 48 hours, and every later search read the zero back as a measurement.
  if (elements.length > 0) writeCache(cacheKey, result);
  return result;
}

export interface SupplementalEnvironment {
  env: OsmEnvironment;
  /** Tiles actually sent to Overpass — genuinely new ground. */
  fetchedTiles: number;
  /** Tiles skipped because an earlier corridor fetch already covered them. */
  reusedTiles: number;
}

/**
 * Corridor environment for routes that reach BEYOND ground already fetched.
 *
 * (Morgan, Aug 23, decision 2 of the enrich-before-discover reorder: "Approve
 * the second osmEnv fetch, but only for genuinely new route geography. Reuse/
 * cache everything from pass 1... Do not blindly redo the full corridor OSM
 * fetch.")
 *
 * fetchOverpassTiles caches by the WHOLE tile set, not per tile, so calling
 * fetchOsmEnvironmentForRoutes a second time with "the same routes plus a few
 * more" is a complete cache miss and re-queries every tile — the exact cost
 * the decision refuses. This tiles both sets identically, subtracts the cells
 * the first fetch already covered, and queries only the remainder. When the
 * discovered routes add no new ground it issues no request at all and says so.
 */
export async function fetchOsmEnvironmentForNewRoutes(
  newRoutes: LngLat[][],
  alreadyCovered: LngLat[][],
): Promise<SupplementalEnvironment> {
  const empty: OsmEnvironment = { points: [], lines: [] };
  const valid = newRoutes.filter((c) => c.length >= 2);
  if (valid.length === 0) return { env: empty, fetchedTiles: 0, reusedTiles: 0 };
  const covered = new Set(
    alreadyCovered
      .filter((c) => c.length >= 2)
      .flatMap((c) => routeTiles(c, 12))
      .map(tileCell),
  );
  const wanted = dedupeTiles(valid.flatMap((c) => routeTiles(c, 12)));
  const fresh = wanted.filter((t) => !covered.has(tileCell(t)));
  const reusedTiles = wanted.length - fresh.length;
  if (fresh.length === 0) return { env: empty, fetchedTiles: 0, reusedTiles };
  // Same cache-key shape as fetchOsmEnvironmentForRoutes on purpose: a repeat
  // search hits the same durable entry whichever function assembled the set.
  const r = (n: number) => Math.round(n * 1000) / 1000;
  const sig = fresh
    .map((t) => `${r(t.south)}_${r(t.west)}`)
    .sort()
    .join("|");
  const cacheKey = `osm-env-multi:v1:${fresh.length}:${sig}`;
  const cached = readCache<OsmEnvironment>(cacheKey);
  if (cached) return { env: cached, fetchedTiles: fresh.length, reusedTiles };
  const elements = await fetchOverpassTiles(fresh, "combined");
  const result = buildEnvironment(elements);
  // AN EMPTY ANSWER IS UNMEASURED, NOT "NOTHING HERE" (rule 20), AND MUST NOT
  // BE REMEMBERED (Morgan, Aug 23). The per-cell layer already refuses to bank
  // empties — the July starvation lesson — but this OUTER cache was writing
  // `{points:[],lines:[]}` for the full TTL whenever upstream failed, which
  // negated it completely: one bad minute made the corridor score on nothing
  // for 48 hours, and every later search read the zero back as a measurement.
  if (elements.length > 0) writeCache(cacheKey, result);
  return { env: result, fetchedTiles: fresh.length, reusedTiles };
}

/** Union of two corridor environments, deduped. Used to fold a supplemental
 *  fetch into the pass-1 environment without double-counting a feature that
 *  sits in both tile sets (a river crossing the seam appears in each). */
export function mergeOsmEnvironments(
  a: OsmEnvironment,
  b: OsmEnvironment,
): OsmEnvironment {
  if (b.points.length === 0 && b.lines.length === 0) return a;
  if (a.points.length === 0 && a.lines.length === 0) return b;
  const points: OsmPointFeature[] = [];
  const seenP = new Set<string>();
  for (const p of [...a.points, ...b.points]) {
    const k = `${p.kind}|${p.lng.toFixed(5)}|${p.lat.toFixed(5)}`;
    if (seenP.has(k)) continue;
    seenP.add(k);
    points.push(p);
  }
  const lines: OsmLineFeature[] = [];
  const seenL = new Set<string>();
  for (const l of [...a.lines, ...b.lines]) {
    const f = l.line[0];
    const t = l.line[l.line.length - 1];
    if (!f || !t) continue;
    const k = `${l.kind}|${l.line.length}|${f[0].toFixed(5)},${f[1].toFixed(5)}|${t[0].toFixed(5)},${t[1].toFixed(5)}`;
    if (seenL.has(k)) continue;
    seenL.add(k);
    lines.push(l);
  }
  return { points, lines };
}

/** Parse merged Overpass elements into the points + lines environment.
 *  Shared by the single-route and multi-route corridor fetchers. */
function buildEnvironment(elements: OverpassEl[]): OsmEnvironment {
  const points: OsmPointFeature[] = [];
  const lines: OsmLineFeature[] = [];

  for (const el of elements) {
    const t = el.tags ?? {};
    const name = typeof t.name === "string" ? t.name : undefined;
    const kinds = classifyElement(t);
    if (kinds.length === 0) continue;

    const linearOnly = kinds.filter((k) => isLinear(k));
    const areaOnly = kinds.filter((k) => !isLinear(k));

    if (linearOnly.length > 0) {
      const line = pickLine(el);
      if (line) {
        for (const k of linearOnly) lines.push({ kind: k, line, name });
      }
    }
    // ALSO emit water area polygons (natural=water, landuse=reservoir) as
    // shoreline lines. The polygon vertices form a closed loop; using them
    // as a line lets scoring.ts measure distance-to-shore directly, instead
    // of distance-to-centroid which can be hundreds of meters off for a
    // big reservoir. Same way Pleasant detects coastlines and rivers.
    if (kinds.includes("water" as OsmAreaKind)) {
      const shoreline = pickLine(el);
      if (shoreline) {
        // Use "lake" as a distinct kind so scoring can label it and filter
        // it alongside coastlines/rivers/canals.
        // CANONICAL KIND, NO CAST (Morgan, Aug 22). This was
        // `kind: "lake" as OsmAreaKind`. "lake" is NOT a member of
        // OsmAreaKind; the cast is what stopped the compiler from noticing,
        // and OSM_BASE_POINTS["lake"] then resolved to undefined, producing
        // NaN * scores for any route that ran within 60m of a shoreline.
        // Confirmed in production: two Pleasantville candidates scored NaN and
        // one of them WON the Pleasant slot.
        //
        // A lake shoreline is water. It takes the existing `water` weight and
        // dimension rather than a new one invented to make a type compile. The
        // user-facing "lake" wording is a DISPLAY label and is preserved
        // separately in waterAdjacency (scoring.ts).
        lines.push({ kind: "water", line: shoreline, name });
      }
    }
    if (areaOnly.length > 0) {
      // For NAMED area-like features (parks, nature reserves, forests, water
      // bodies, etc.), the centroid alone is misleading for huge polygons —
      // a 5km-wide park's center is often nowhere near the road that grazes
      // its edge. Sample points along the way's perimeter so the route
      // proximity test in scoring.ts can match the boundary, not the center.
      const samplePts = name && Array.isArray(el.geometry) && el.geometry.length > 0
        ? sampleAreaPoints(el)
        : [pickPoint(el)].filter(Boolean) as { lng: number; lat: number }[];
      for (const p of samplePts) {
        for (const k of areaOnly) points.push({ kind: k, ...p, name });
      }
    }
  }

  return { points, lines };
}

function isLinear(k: OsmAreaKind): boolean {
  // "water" (lakes/reservoirs) ALSO appears as a line — see the dual-emit
  // logic in the extraction loop. Their shoreline polyline is what a road
  // is actually adjacent to, not the polygon centroid.
  // The comment said this for months; the `case` was missing, so isLinear
  // returned false for water and the extraction reached for an off-union
  // "lake" instead. Now stated in code, not only in prose.
  switch (k) {
    case "river":
    case "canal":
    case "coastline":
    case "water":
    case "residential":
    case "living_street":
    case "pedestrian":
    case "path":
    case "cycleway":
    case "secondary":
    case "tertiary":
    case "secondary_link":
    case "tertiary_link":
    case "no_sidewalk":
    case "motorway":
    case "trunk":
    case "primary":
    case "motorway_link":
    case "trunk_link":
    case "primary_link":
    case "parking_aisle":
    case "dead_end":
      return true;
    default:
      return false;
  }
}

function classifyElement(t: Record<string, string>): OsmAreaKind[] {
  const out: OsmAreaKind[] = [];
  if (t.leisure === "park") out.push("park");
  if (t.leisure === "garden") out.push("garden");
  if (t.leisure === "nature_reserve" || t.boundary === "national_park" || t.boundary === "protected_area") out.push("nature_reserve");
  if (t.leisure === "recreation_ground" || t.leisure === "common" || t.leisure === "village_green" || t.landuse === "recreation_ground") out.push("recreation_ground");
  if (t.landuse === "farmland" || t.landuse === "farmyard") out.push("farmland");
  if (t.landuse === "meadow" || t.natural === "grassland") out.push("meadow");
  if (t.landuse === "orchard" || t.landuse === "vineyard" || t.landuse === "allotments") out.push("orchard");
  if (t.landuse === "grass" || ["heath","scrub","fell","moor"].includes(t.natural)) out.push("open_green");
  if (t.tourism === "viewpoint" || t.natural === "peak" || t.natural === "hill") out.push("viewpoint");
  if (t.natural === "water" || t.landuse === "reservoir") out.push("water");
  if (t.natural === "wood") out.push("wood");
  if (t.landuse === "forest") out.push("forest");
  if (t.waterway === "river") out.push("river");
  if (t.waterway === "canal") out.push("canal");
  if (t.natural === "coastline") out.push("coastline");
  if (t.historic) out.push("historic");
  if (t.tourism === "attraction") out.push("attraction");

  if (t.landuse === "residential") out.push("residential_land");
  switch (t.highway) {
    case "residential":     out.push("residential"); break;
    case "living_street":   out.push("living_street"); break;
    case "pedestrian":      out.push("pedestrian"); break;
    case "path":            out.push("path"); break;
    case "footway":         out.push("path"); break;
    case "track":           out.push("path"); break;
    case "bridleway":       out.push("path"); break;
    case "steps":           out.push("path"); break;
    case "cycleway":        out.push("cycleway"); break;
    case "secondary":       out.push("secondary"); break;
    case "tertiary":        out.push("tertiary"); break;
    case "secondary_link":  out.push("secondary_link"); break;
    case "tertiary_link":   out.push("tertiary_link"); break;
    case "motorway":        out.push("motorway"); break;
    case "trunk":           out.push("trunk"); break;
    case "primary":         out.push("primary"); break;
    case "motorway_link":   out.push("motorway_link"); break;
    case "trunk_link":      out.push("trunk_link"); break;
    case "primary_link":    out.push("primary_link"); break;
  }
  if (t.sidewalk === "no" && ["residential", "secondary", "tertiary", "primary"].includes(t.highway)) {
    out.push("no_sidewalk");
  }
  if (t.amenity === "library") out.push("library");
  if (t.building === "church" || t.amenity === "place_of_worship") out.push("church");

  if (t.landuse === "industrial") out.push("industrial");
  if (t.amenity === "parking") out.push("parking");
  if (t.service === "parking_aisle") out.push("parking_aisle");

  // (#127) Dead-end / no-thru-traffic ways. Two patterns:
  //   - noexit=yes is the explicit "this way doesn't go through" tag
  //   - highway=service + service=driveway is the cul-de-sac connector
  //     pattern (private driveways into a single residence/lot).
  // Either signal qualifies the way as a dead-end candidate that
  // routes should not detour into mid-trip.
  if (t.noexit === "yes" || (t.highway === "service" && t.service === "driveway")) {
    out.push("dead_end");
  }

  return out;
}
